import React from "react";
import Layout from "../components/Layout";
import Seo from "../components/SEO";
import SplashImage from "../components/SplashImage";
import { getImage } from "gatsby-plugin-image";
import { graphql, Link } from "gatsby";
import { useIntl } from "gatsby-plugin-react-intl";

export const pageQuery = graphql`
  {
    file(relativePath: { eq: "highway-intersection.jpg" }) {
      ...SplashImage
    }
  }
`;

export default function GDPRPage({ data }) {
  const intl = useIntl();
  return (
    <Layout intl={intl} title="Data and Privacy Declaration">
      <Seo title="GDPR Info" />
      <SplashImage image={getImage(data.file)} />
      <main>
        <section className="bg-blue-100 py-10 dsgvo" id="german-version">
          <div className="container max-w-screen-lg mx-auto text-center px-5">
            <a href="#english-version">English Version Below</a>
            <hr className="border-uclight my-8 w-3/4 mx-auto" />
            <h1>Datenschutzerklärung</h1>
            <h2>Einleitung und Überblick</h2>
            <p>
              Wir haben diese Datenschutzerklärung (Fassung 13.07.2021-111779921) verfasst, um Ihnen gemäß der Vorgaben der
              <a href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&tid=111779921" target="_blank" rel="noreferrer">
                Datenschutz-Grundverordnung (EU) 2016/679
              </a>
              und anwendbaren nationalen Gesetzen zu erklären, welche personenbezogenen Daten (kurz Daten) wir als Verantwortliche – und die von uns
              beauftragten Auftragsverarbeiter (z. B. Provider) – verarbeiten, zukünftig verarbeiten werden und welche rechtmäßigen Möglichkeiten Sie
              haben. Die verwendeten Begriffe sind geschlechtsneutral zu verstehen.
              <br />
              <strong>Kurz gesagt:</strong> Wir informieren Sie umfassend über Daten, die wir über Sie verarbeiten.
            </p>
            <p>
              Datenschutzerklärungen klingen für gewöhnlich sehr technisch und verwenden juristische Fachbegriffe. Diese Datenschutzerklärung soll
              Ihnen hingegen die wichtigsten Dinge so einfach und transparent wie möglich beschreiben. Soweit es der Transparenz förderlich ist,
              werden technische <strong>Begriffe leserfreundlich erklärt</strong>, <strong>Links</strong> zu weiterführenden Informationen geboten und
              <strong>Grafiken</strong> zum Einsatz gebracht. Wir informieren damit in klarer und einfacher Sprache, dass wir im Rahmen unserer
              Geschäftstätigkeiten nur dann personenbezogene Daten verarbeiten, wenn eine entsprechende gesetzliche Grundlage gegeben ist. Das ist
              sicher nicht möglich, wenn man möglichst knappe, unklare und juristisch-technische Erklärungen abgibt, so wie sie im Internet oft
              Standard sind, wenn es um Datenschutz geht. Ich hoffe, Sie finden die folgenden Erläuterungen interessant und informativ und vielleicht
              ist die eine oder andere Information dabei, die Sie noch nicht kannten.
              <br />
              Wenn trotzdem Fragen bleiben, möchten wir Sie bitten, sich and die im Impressum genannte verantwortliche Stelle zu wenden, den
              vorhandenen Links zu folgen und sich weitere Informationen auf Drittseiten anzusehen.
            </p>
            <h2 id="anwendungsbereich">Anwendungsbereich</h2>
            <p>
              Diese Datenschutzerklärung gilt für alle von uns im Unternehmen verarbeiteten personenbezogenen Daten und für alle personenbezogenen
              Daten, die von uns beauftragte Firmen (Auftragsverarbeiter) verarbeiten. Mit personenbezogenen Daten meinen wir Informationen im Sinne
              des Art. 4 Nr. 1 DSGVO wie zum Beispiel Name, E-Mail-Adresse und postalische Anschrift einer Person. Die Verarbeitung personenbezogener
              Daten sorgt dafür, dass wir unsere Dienstleistungen und Produkte anbieten und abrechnen können, sei es online oder offline. Der
              Anwendungsbereich dieser Datenschutzerklärung umfasst:
            </p>
            <ul>
              <li>alle Onlineauftritte (Websites, Onlineshops), die wir betreiben</li>
              <li>Social Media Auftritte und E-Mail-Kommunikation</li>
              <li>mobile Apps für Smartphones und andere Geräte</li>
            </ul>
            <p>
              <strong>Kurz gesagt:</strong> Die Datenschutzerklärung gilt für alle Bereiche, in denen personenbezogene Daten im Unternehmen über die
              genannten Kanäle strukturiert verarbeitet werden. Sollten wir außerhalb dieser Kanäle mit Ihnen in Rechtsbeziehungen eintreten, werden
              wir Sie gegebenenfalls gesondert informieren.
            </p>
            <h2 id="rechtsgrundlagen">Rechtsgrundlagen</h2>
            <p>
              In der folgenden Datenschutzerklärung geben wir Ihnen transparente Informationen zu den rechtlichen Grundsätzen und Vorschriften, also
              den Rechtsgrundlagen der Datenschutz-Grundverordnung, die uns ermöglichen, personenbezogene Daten zu verarbeiten.
              <br />
              Was das EU-Recht betrifft, beziehen wir uns auf die VERORDNUNG (EU) 2016/679 DES EUROPÄISCHEN PARLAMENTS UND DES RATES vom 27. April
              2016. Diese Datenschutz-Grundverordnung der EU können Sie selbstverständlich online auf EUR-Lex, dem Zugang zum EU-Recht, unter
              <a href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&tid=111779921" target="_blank" rel="noreferrer">
                https://eur-lex.europa.eu/legal-content/DE/TXT/?uri=celex%3A32016R0679
              </a>
              nachlesen.
            </p>
            <p>Wir verarbeiten Ihre Daten nur, wenn mindestens eine der folgenden Bedingungen zutrifft:</p>
            <ol>
              <li>
                <strong>Einwilligung</strong> (Artikel 6 Absatz 1 lit. a DSGVO): Sie haben uns Ihre Einwilligung gegeben, Daten zu einem bestimmten
                Zweck zu verarbeiten. Ein Beispiel wäre die Speicherung Ihrer eingegebenen Daten eines Kontaktformulars.
              </li>
              <li>
                <strong>Vertrag</strong> (Artikel 6 Absatz 1 lit. b DSGVO): Um einen Vertrag oder vorvertragliche Verpflichtungen mit Ihnen zu
                erfüllen, verarbeiten wir Ihre Daten. Wenn wir zum Beispiel einen Kaufvertrag mit Ihnen abschließen, benötigen wir vorab
                personenbezogene Informationen.
              </li>
              <li>
                <strong>Rechtliche Verpflichtung</strong> (Artikel 6 Absatz 1 lit. c DSGVO): Wenn wir einer rechtlichen Verpflichtung unterliegen,
                verarbeiten wir Ihre Daten. Zum Beispiel sind wir gesetzlich verpflichtet Rechnungen für die Buchhaltung aufzuheben. Diese enthalten
                in der Regel personenbezogene Daten.
              </li>
              <li>
                <strong>Berechtigte Interessen</strong> (Artikel 6 Absatz 1 lit. f DSGVO): Im Falle berechtigter Interessen, die Ihre Grundrechte
                nicht einschränken, behalten wir uns die Verarbeitung personenbezogener Daten vor. Wir müssen zum Beispiel gewisse Daten verarbeiten,
                um unsere Website sicher und wirtschaftlich effizient betreiben zu können. Diese Verarbeitung ist somit ein berechtigtes Interesse.
              </li>
            </ol>
            <p>
              Weitere Bedingungen wie die Wahrnehmung von Aufnahmen im öffentlichen Interesse und Ausübung öffentlicher Gewalt sowie dem Schutz
              lebenswichtiger Interessen treten bei uns in der Regel nicht auf. Soweit eine solche Rechtsgrundlage doch einschlägig sein sollte, wird
              diese an der entsprechenden Stelle ausgewiesen.
            </p>
            <p>Zusätzlich zu der EU-Verordnung gelten auch noch nationale Gesetze:</p>
            <ul>
              <li>
                In <strong>Österreich</strong> ist dies das Bundesgesetz zum Schutz natürlicher Personen bei der Verarbeitung personenbezogener Daten
                (<strong>Datenschutzgesetz</strong>), kurz <strong>DSG</strong>.
              </li>
              <li>
                In <strong>Deutschland</strong> gilt das <strong>Bundesdatenschutzgesetz</strong>, kurz<strong> BDSG</strong>.
              </li>
            </ul>
            <p>Sofern weitere regionale oder nationale Gesetze zur Anwendung kommen, informieren wir Sie in den folgenden Abschnitten darüber.</p>
            <h2 id="kontaktdaten-verantwortliche">Kontaktdaten des Verantwortlichen</h2>
            <p>
              Sollten Sie Fragen zum Datenschutz haben, finden Sie nachfolgend die Kontaktdaten der verantwortlichen Person bzw. Stelle:
              <br />
              Impressum:&nbsp;
              <Link to="/imprint">https://www.ubicube.eu/imprint</Link>
            </p>
            <h2 id="speicherdauer">Speicherdauer</h2>
            <p>
              Dass wir personenbezogene Daten nur so lange speichern, wie es für die Bereitstellung unserer Dienstleistungen und Produkte unbedingt
              notwendig ist, gilt als generelles Kriterium bei uns. Das bedeutet, dass wir personenbezogene Daten löschen, sobald der Grund für die
              Datenverarbeitung nicht mehr vorhanden ist. In einigen Fällen sind wir gesetzlich dazu verpflichtet, bestimmte Daten auch nach Wegfall
              des ursprüngliches Zwecks zu speichern, zum Beispiel zu Zwecken der Buchführung.
            </p>
            <p>
              Sollten Sie die Löschung Ihrer Daten wünschen oder die Einwilligung zur Datenverarbeitung widerrufen, werden die Daten so rasch wie
              möglich und soweit keine Pflicht zur Speicherung besteht, gelöscht.
            </p>
            <p>
              Über die konkrete Dauer der jeweiligen Datenverarbeitung informieren wir Sie weiter unten, sofern wir weitere Informationen dazu haben.
            </p>
            <h2 id="rechte-dsgvo">Rechte laut Datenschutzgrundverordnung</h2>
            <p>
              Laut Artikel 13 DSGVO stehen Ihnen die folgenden Rechte zu, damit es zu einer fairen und transparenten Verarbeitung von Daten kommt:
            </p>
            <ul>
              <li>
                Sie haben laut Artikel 15 DSGVO ein Auskunftsrecht darüber, ob wir Daten von Ihnen verarbeiten. Sollte das zutreffen, haben Sie Recht
                darauf eine Kopie der Daten zu erhalten und die folgenden Informationen zu erfahren:
                <ul>
                  <li>zu welchem Zweck wir die Verarbeitung durchführen;</li>
                  <li>die Kategorien, also die Arten von Daten, die verarbeitet werden;</li>
                  <li>wer diese Daten erhält und wenn die Daten an Drittländer übermittelt werden, wie die Sicherheit garantiert werden kann;</li>
                  <li>wie lange die Daten gespeichert werden;</li>
                  <li>
                    das Bestehen des Rechts auf Berichtigung, Löschung oder Einschränkung der Verarbeitung und dem Widerspruchsrecht gegen die
                    Verarbeitung;
                  </li>
                  <li>dass Sie sich bei einer Aufsichtsbehörde beschweren können (Links zu diesen Behörden finden Sie weiter unten);</li>
                  <li>die Herkunft der Daten, wenn wir sie nicht bei Ihnen erhoben haben;</li>
                  <li>
                    ob Profiling durchgeführt wird, ob also Daten automatisch ausgewertet werden, um zu einem persönlichen Profil von Ihnen zu
                    gelangen.
                  </li>
                </ul>
              </li>
              <li>
                Sie haben laut Artikel 16 DSGVO ein Recht auf Berichtigung der Daten, was bedeutet, dass wir Daten richtig stellen müssen, falls Sie
                Fehler finden.
              </li>
              <li>
                Sie haben laut Artikel 17 DSGVO das Recht auf Löschung („Recht auf Vergessenwerden“), was konkret bedeutet, dass Sie die Löschung
                Ihrer Daten verlangen dürfen.
              </li>
              <li>
                Sie haben laut Artikel 18 DSGVO das Recht auf Einschränkung der Verarbeitung, was bedeutet, dass wir die Daten nur mehr speichern
                dürfen aber nicht weiter verwenden.
              </li>
              <li>
                Sie haben laut Artikel 20 DSGVO das Recht auf Datenübertragbarkeit, was bedeutet, dass wir Ihnen auf Anfrage Ihre Daten in einem
                gängigen Format zur Verfügung stellen.
              </li>
              <li>
                Sie haben laut Artikel 21 DSGVO ein Widerspruchsrecht, welches nach Durchsetzung eine Änderung der Verarbeitung mit sich bringt.
                <ul>
                  <li>
                    Wenn die Verarbeitung Ihrer Daten auf Artikel 6 Abs. 1 lit. e (öffentliches Interesse, Ausübung öffentlicher Gewalt) oder Artikel
                    6 Abs. 1 lit. f (berechtigtes Interesse) basiert, können Sie gegen die Verarbeitung Widerspruch einlegen. Wir prüfen danach so
                    rasch wie möglich, ob wir diesem Widerspruch rechtlich nachkommen können.
                  </li>
                  <li>
                    Werden Daten verwendet, um Direktwerbung zu betreiben, können Sie jederzeit gegen diese Art der Datenverarbeitung widersprechen.
                    Wir dürfen Ihre Daten danach nicht mehr für Direktmarketing verwenden.
                  </li>
                  <li>
                    Werden Daten verwendet, um Profiling zu betreiben, können Sie jederzeit gegen diese Art der Datenverarbeitung widersprechen. Wir
                    dürfen Ihre Daten danach nicht mehr für Profiling verwenden.
                  </li>
                </ul>
              </li>
              <li>
                Sie haben laut Artikel 22 DSGVO unter Umständen das Recht, nicht einer ausschließlich auf einer automatisierten Verarbeitung (zum
                Beispiel Profiling) beruhenden Entscheidung unterworfen zu werden.
              </li>
            </ul>
            <p>
              Wenn Sie glauben, dass die Verarbeitung Ihrer Daten gegen das Datenschutzrecht verstößt oder Ihre datenschutzrechtlichen Ansprüche in
              sonst einer Weise verletzt worden sind, können Sie sich bei der Aufsichtsbehörde beschweren. Diese ist für Österreich die
              Datenschutzbehörde, deren Website Sie unter
              <a href="https://www.dsb.gv.at/?tid=111779921" target="_blank" rel="noreferrer">
                https://www.dsb.gv.at/
              </a>
              finden und für Deutschland können Sie sich an die
              <a href="https://www.bfdi.bund.de" target="_blank" rel="noreferrer">
                Bundesbeauftragte für den Datenschutz und die Informationsfreiheit (BfDI)
              </a>
              wenden.
            </p>
            <h2 id="webhosting">Webhosting</h2>
            <ul>
              <li>
                <strong>Webhosting Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: professionelles Hosting der Website und Absicherung des Betriebs</li>
              <li>
                Verarbeitete Daten: IP-Adresse, Zeitpunkt des Websitebesuchs, verwendeter Browser und weitere Daten. Mehr Details dazu finden Sie
                weiter unten bzw. beim jeweils eingesetzten Webhosting Provider.
              </li>
              <li>Speicherdauer: abhängig vom jeweiligen Provider, aber in der Regel 2 Wochen</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit.f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist Webhosting?</h3>
            <p>
              Wenn Sie heutzutage Websites besuchen, werden gewisse Informationen – auch personenbezogene Daten – automatisch erstellt und
              gespeichert, so auch auf dieser Website. Diese Daten sollten möglichst sparsam und nur mit Begründung verarbeitet werden. Mit Website
              meinen wir übrigens die Gesamtheit aller Webseiten auf einer Domain, d.h. alles von der Startseite (Homepage) bis hin zur aller letzten
              Unterseite (wie dieser hier). Mit Domain meinen wir zum Beispiel beispiel.at oder musterbeispiel.com.
            </p>
            <p>
              Wenn Sie eine Website auf einem Bildschirm ansehen möchten, verwenden Sie dafür ein Programm, das sich Webbrowser nennt. Sie kennen
              vermutlich einige Webbrowser beim Namen: Google Chrome, Microsoft Edge, Mozilla Firefox und Apple Safari.
            </p>
            <p>
              Dieser Webbrowser muss sich zu einem anderen Computer verbinden, wo der Code der Website gespeichert ist: dem Webserver. Der Betrieb
              eines Webservers ist eine komplizierte und aufwendige Aufgabe, weswegen dies in der Regel von professionellen Anbietern, den Providern,
              übernommen wird. Diese bieten Webhosting an und sorgen damit für eine verlässliche und fehlerfreie Speicherung der Daten von Websites.
            </p>
            <p>
              Bei der Verbindungsaufnahme des Browsers auf Ihrem Computer (Desktop, Laptop, Smartphone) und während der Datenübertragung zu und vom
              Webserver kann es zu einer Verarbeitung personenbezogener Daten kommen. Einerseits speichert Ihr Computer Daten, andererseits muss auch
              der Webserver Daten eine Zeit lang speichern, um einen ordentlichen Betrieb zu gewährleisten.
            </p>
            <p>Zur Veranschaulichung:</p>
            <p>
              <img src="https://www.adsimple.at/wp-content/uploads/2018/03/browser-und-webserver.svg" alt="Browser und Webserver" />
            </p>
            <h3>Warum verarbeiten wir personenbezogene Daten?</h3>
            <p>Die Zwecke der Datenverarbeitung sind:</p>
            <ol>
              <li>Professionelles Hosting der Website und Absicherung des Betriebs</li>
              <li>zur Aufrechterhaltung der Betriebs- und IT-Sicherheit</li>
              <li>
                Anonyme Auswertung des Zugriffsverhaltens zur Verbesserung unseres Angebots und ggf. zur Strafverfolgung bzw. Verfolgung von
                Ansprüchen
              </li>
            </ol>
            <h3>Welche Daten werden verarbeitet?</h3>
            <p>
              Auch während Sie unsere Website jetzt gerade besuchen, speichert unser Webserver, das ist der Computer auf dem diese Webseite
              gespeichert ist, in der Regel automatisch Daten wie
            </p>
            <ul>
              <li>
                die komplette Internetadresse (URL) der aufgerufenen Webseite (z. B.
                https://www.beispielwebsite.com/beispielunterseite.html?tid=111779921)
              </li>
              <li>Browser und Browserversion (z. B. Chrome 87)</li>
              <li>das verwendete Betriebssystem (z. B. Windows 10)</li>
              <li>die Adresse (URL) der zuvor besuchten Seite (Referrer URL) (z. B. https://www.beispielquellsite.com/vondabinichgekommen.html/)</li>
              <li>den Hostnamen und die IP-Adresse des Geräts von welchem aus zugegriffen wird (z. B. COMPUTERNAME und 194.23.43.121)</li>
              <li>Datum und Uhrzeit</li>
              <li>in Dateien, den sogenannten Webserver-Logfiles</li>
            </ul>
            <h3>Wie lange werden Daten gespeichert?</h3>
            <p>
              In der Regel werden die oben genannten Daten zwei Wochen gespeichert und danach automatisch gelöscht. Wir geben diese Daten nicht
              weiter, können jedoch nicht ausschließen, dass diese Daten beim Vorliegen von rechtswidrigem Verhalten von Behörden eingesehen werden.
            </p>
            <p>
              <strong>Kurz gesagt:</strong> Ihr Besuch wird durch unseren Provider (Firma, die unsere Website auf speziellen Computern (Servern)
              laufen lässt), protokolliert, aber wir geben Ihre Daten nicht ohne Zustimmung weiter!
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Die Rechtmäßigkeit der Verarbeitung personenbezogener Daten im Rahmen des Webhosting ergibt sich aus Art. 6 Abs. 1 lit. f DSGVO (Wahrung
              der berechtigten Interessen), denn die Nutzung von professionellem Hosting bei einem Provider ist notwendig, um das Unternehmen im
              Internet sicher und nutzerfreundlich präsentieren und Angriffe und Forderungen hieraus gegebenenfalls verfolgen zu können.
            </p>
            <h2 id="cookies">Cookies</h2>
            <ul>
              <li>
                <strong>Cookies Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>
                Zweck: abhängig vom jeweiligen Cookie. Mehr Details dazu finden Sie weiter unten bzw. beim Hersteller der Software, der das Cookie
                setzt.
              </li>
              <li>
                Verarbeitete Daten: Abhängig vom jeweils eingesetzten Cookie. Mehr Details dazu finden Sie weiter unten bzw. beim Hersteller der
                Software, der das Cookie setzt.
              </li>
              <li>Speicherdauer: abhängig vom jeweiligen Cookie, kann von Stunden bis hin zu Jahren variieren</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit.f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was sind Cookies?</h3>
            <p>
              Unsere Website verwendet HTTP-Cookies, um nutzerspezifische Daten zu speichern.
              <br />
              Im Folgenden erklären wir, was Cookies sind und warum Sie genutzt werden, damit Sie die folgende Datenschutzerklärung besser verstehen.
            </p>
            <p>
              Immer wenn Sie durch das Internet surfen, verwenden Sie einen Browser. Bekannte Browser sind beispielsweise Chrome, Safari, Firefox,
              Internet Explorer und Microsoft Edge. Die meisten Websites speichern kleine Text-Dateien in Ihrem Browser. Diese Dateien nennt man
              Cookies.
            </p>
            <p>
              Eines ist nicht von der Hand zu weisen: Cookies sind echt nützliche Helferlein. Fast alle Websites verwenden Cookies. Genauer gesprochen
              sind es HTTP-Cookies, da es auch noch andere Cookies für andere Anwendungsbereiche gibt. HTTP-Cookies sind kleine Dateien, die von
              unserer Website auf Ihrem Computer gespeichert werden. Diese Cookie-Dateien werden automatisch im Cookie-Ordner, quasi dem “Hirn” Ihres
              Browsers, untergebracht. Ein Cookie besteht aus einem Namen und einem Wert. Bei der Definition eines Cookies müssen zusätzlich ein oder
              mehrere Attribute angegeben werden.
            </p>
            <p>
              Cookies speichern gewisse Nutzerdaten von Ihnen, wie beispielsweise Sprache oder persönliche Seiteneinstellungen. Wenn Sie unsere Seite
              wieder aufrufen, übermittelt Ihr Browser die „userbezogenen“ Informationen an unsere Seite zurück. Dank der Cookies weiß unsere Website,
              wer Sie sind und bietet Ihnen die Einstellung, die Sie gewohnt sind. In einigen Browsern hat jedes Cookie eine eigene Datei, in anderen
              wie beispielsweise Firefox sind alle Cookies in einer einzigen Datei gespeichert.
            </p>
            <p>
              Die folgende Grafik zeigt eine mögliche Interaktion zwischen einem Webbrowser wie z. B. Chrome und dem Webserver. Dabei fordert der
              Webbrowser eine Website an und erhält vom Server ein Cookie zurück, welches der Browser erneut verwendet, sobald eine andere Seite
              angefordert wird.
            </p>
            <p>
              <img
                src="https://www.adsimple.at/wp-content/uploads/2018/03/http-cookie-interaction.svg"
                alt="HTTP Cookie Interaktion zwischen Browser und Webserver"
              />
            </p>
            <p>
              Es gibt sowohl Erstanbieter Cookies als auch Drittanbieter-Cookies. Erstanbieter-Cookies werden direkt von unserer Seite erstellt,
              Drittanbieter-Cookies werden von Partner-Websites (z.B. Google Analytics) erstellt. Jedes Cookie ist individuell zu bewerten, da jedes
              Cookie andere Daten speichert. Auch die Ablaufzeit eines Cookies variiert von ein paar Minuten bis hin zu ein paar Jahren. Cookies sind
              keine Software-Programme und enthalten keine Viren, Trojaner oder andere „Schädlinge“. Cookies können auch nicht auf Informationen Ihres
              PCs zugreifen.
            </p>
            <p>So können zum Beispiel Cookie-Daten aussehen:</p>
            <p>
              <strong>Name:</strong> _ga
              <br />
              <strong>Wert:</strong>&nbsp;GA1.2.1326744211.152111779921-9
              <br />
              <strong>Verwendungszweck:</strong> Unterscheidung der Websitebesucher
              <br />
              <strong>Ablaufdatum:</strong>&nbsp;nach 2 Jahren
            </p>
            <p>Diese Mindestgrößen sollte ein Browser unterstützen können:</p>
            <ul>
              <li>Mindestens 4096 Bytes pro Cookie</li>
              <li>Mindestens 50 Cookies pro Domain</li>
              <li>Mindestens 3000 Cookies insgesamt</li>
            </ul>
            <h3>Welche Arten von Cookies gibt es?</h3>
            <p>
              Die Frage welche Cookies wir im Speziellen verwenden, hängt von den verwendeten Diensten ab und wird in den folgenden Abschnitten der
              Datenschutzerklärung geklärt. An dieser Stelle möchten wir kurz auf die verschiedenen Arten von HTTP-Cookies eingehen.
            </p>
            <p>Man kann 4 Arten von Cookies unterscheiden:</p>
            <p>
              <strong>
                Unerlässliche Cookies
                <br />
              </strong>
              Diese Cookies sind nötig, um grundlegende Funktionen der Website sicherzustellen. Zum Beispiel braucht es diese Cookies, wenn ein User
              ein Produkt in den Warenkorb legt, dann auf anderen Seiten weitersurft und später erst zur Kasse geht. Durch diese Cookies wird der
              Warenkorb nicht gelöscht, selbst wenn der User sein Browserfenster schließt.
            </p>
            <p>
              <strong>
                Zweckmäßige Cookies
                <br />
              </strong>
              Diese Cookies sammeln Infos über das Userverhalten und ob der User etwaige Fehlermeldungen bekommt. Zudem werden mithilfe dieser Cookies
              auch die Ladezeit und das Verhalten der Website bei verschiedenen Browsern gemessen.
            </p>
            <p>
              <strong>
                Zielorientierte Cookies
                <br />
              </strong>
              Diese Cookies sorgen für eine bessere Nutzerfreundlichkeit. Beispielsweise werden eingegebene Standorte, Schriftgrößen oder
              Formulardaten gespeichert.
            </p>
            <p>
              <strong>
                Werbe-Cookies
                <br />
              </strong>
              Diese Cookies werden auch Targeting-Cookies genannt. Sie dienen dazu dem User individuell angepasste Werbung zu liefern. Das kann sehr
              praktisch, aber auch sehr nervig sein.
            </p>
            <p>
              Üblicherweise werden Sie beim erstmaligen Besuch einer Website gefragt, welche dieser Cookiearten Sie zulassen möchten. Und natürlich
              wird diese Entscheidung auch in einem Cookie gespeichert.
            </p>
            <p>
              Wenn Sie mehr über Cookies wissen möchten und technische Dokumentationen nicht scheuen, empfehlen wir
              <a href="https://tools.ietf.org/html/rfc6265" target="_blank" rel="noreferrer">
                https://tools.ietf.org/html/rfc6265
              </a>
              , dem Request for Comments der Internet Engineering Task Force (IETF) namens “HTTP State Management Mechanism”.
            </p>
            <h3>Zweck der Verarbeitung über Cookies</h3>
            <p>
              Der Zweck ist letztendlich abhängig vom jeweiligen Cookie. Mehr Details dazu finden Sie weiter unten bzw. beim Hersteller der Software,
              die das Cookie setzt.
            </p>
            <h3>Welche Daten werden verarbeitet?</h3>
            <p>
              Cookies sind kleine Gehilfen für eine viele verschiedene Aufgaben. Welche Daten in Cookies gespeichert werden, kann man leider nicht
              verallgemeinern, aber wir werden Sie im Rahmen der folgenden Datenschutzerklärung über die verarbeiteten bzw. gespeicherten Daten
              informieren.
            </p>
            <h3>Speicherdauer von Cookies</h3>
            <p>
              Die Speicherdauer hängt vom jeweiligen Cookie ab und wird weiter unter präzisiert. Manche Cookies werden nach weniger als einer Stunde
              gelöscht, andere können mehrere Jahre auf einem Computer gespeichert bleiben.
            </p>
            <p>
              Sie haben außerdem selbst Einfluss auf die Speicherdauer. Sie können über ihren Browser sämtliche Cookies jederzeit manuell löschen
              (siehe auch unten “Widerspruchsrecht”). Ferner werden Cookies, die auf einer Einwilligung beruhen, spätestens nach Widerruf Ihrer
              Einwilligung gelöscht, wobei die Rechtmäßigkeit der Speicherung bis dahin unberührt bleibt.
            </p>
            <h3>Widerspruchsrecht – wie kann ich Cookies löschen?</h3>
            <p>
              Wie und ob Sie Cookies verwenden wollen, entscheiden Sie selbst. Unabhängig von welchem Service oder welcher Website die Cookies
              stammen, haben Sie immer die Möglichkeit Cookies zu löschen, zu deaktivieren oder nur teilweise zuzulassen. Zum Beispiel können Sie
              Cookies von Drittanbietern blockieren, aber alle anderen Cookies zulassen.
            </p>
            <p>
              Wenn Sie feststellen möchten, welche Cookies in Ihrem Browser gespeichert wurden, wenn Sie Cookie-Einstellungen ändern oder löschen
              wollen, können Sie dies in Ihren Browser-Einstellungen finden:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noopener noreferrer">
                Chrome: Cookies in Chrome löschen, aktivieren und verwalten
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/de-at/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noopener noreferrer">
                Safari: Verwalten von Cookies und Websitedaten mit Safari
              </a>
            </p>
            <p>
              <a
                href="https://support.mozilla.org/de/kb/cookies-und-website-daten-in-firefox-loschen?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firefox: Cookies löschen, um Daten zu entfernen, die Websites auf Ihrem Computer abgelegt haben
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Internet Explorer: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/4027947/windows-delete-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Edge: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              Falls Sie grundsätzlich keine Cookies haben wollen, können Sie Ihren Browser so einrichten, dass er Sie immer informiert, wenn ein
              Cookie gesetzt werden soll. So können Sie bei jedem einzelnen Cookie entscheiden, ob Sie das Cookie erlauben oder nicht. Die
              Vorgangsweise ist je nach Browser verschieden. Am besten Sie suchen die Anleitung in Google mit dem Suchbegriff “Cookies löschen Chrome”
              oder “Cookies deaktivieren Chrome” im Falle eines Chrome Browsers.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Seit 2009 gibt es die sogenannten „Cookie-Richtlinien“. Darin ist festgehalten, dass das Speichern von Cookies eine
              <strong>Einwilligung</strong> (Artikel 6 Abs. 1 lit. a DSGVO) von Ihnen verlangt. Innerhalb der EU-Länder gibt es allerdings noch sehr
              unterschiedliche Reaktionen auf diese Richtlinien. In Österreich erfolgte aber die Umsetzung dieser Richtlinie in § 96 Abs. 3 des
              Telekommunikationsgesetzes (TKG). In Deutschland wurden die Cookie-Richtlinien nicht als nationales Recht umgesetzt. Statulessen
              erfolgte die Umsetzung dieser Richtlinie weitgehend in § 15 Abs.3 des Telemediengesetzes (TMG).
            </p>
            <p>
              Für unbedingt notwendige Cookies, auch soweit keine Einwilligung vorliegt. bestehen <strong>berechtigte Interessen</strong> (Artikel 6
              Abs. 1 lit. f DSGVO), die in den meisten Fällen wirtschaftlicher Natur sind. Wir möchten den Besuchern der Website eine angenehme
              Benutzererfahrung bescheren und dafür sind bestimmte Cookies oft unbedingt notwendig.
            </p>
            <p>
              Soweit nicht unbedingt erforderliche Cookies zum Einsatz kommen, geschieht dies nur im Falle Ihrer Einwilligung. Rechtsgrundlage ist
              insoweit Art. 6 Abs. 1 lit. a DSGVO.
            </p>
            <p>
              In den folgenden Abschnitten werden Sie genauer über den Einsatz von Cookies informiert, sofern eingesetzte Software Cookies verwendet.
            </p>
            <h2 id="google-analytics-datenschutzerklaerung">Google Analytics Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>Google Analytics Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Auswertung der Besucherinformationen zur Optimierung des Webangebots.</li>
              <li>
                Verarbeitete Daten: Zugriffsstatistiken, die Daten wie Standorte der Zugriffe, Gerätedaten, Zugriffsdauer und Zeitpunkt,
                Navigationsverhalten, Klickverhalten und IP-Adressen enthalten. Mehr Details dazu finden Sie weiter unten in dieser
                Datenschutzerklärung.
              </li>
              <li>Speicherdauer: abhängig von den verwendeten Properties</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist Google Analytics?</h3>
            <p>
              Wir verwenden auf unserer Website das Analyse-Tracking Tool Google Analytics (GA) des amerikanischen Unternehmens Google Inc. Für den
              europäischen Raum ist das Unternehmen Google Ireland Limited (Gordon House, Barrow Street Dublin 4, Irland) für alle Google-Dienste
              verantwortlich. Google Analytics sammelt Daten über Ihre Handlungen auf unserer Website. Wenn Sie beispielsweise einen Link anklicken,
              wird diese Aktion in einem Cookie gespeichert und an Google Analytics versandt. Mithilfe der Berichte, die wir von Google Analytics
              erhalten, können wir unsere Website und unser Service besser an Ihre Wünsche anpassen. Im Folgenden gehen wir näher auf das
              Tracking-Tool ein und informieren Sie vor allem darüber, welche Daten gespeichert werden und wie Sie das verhindern können.
            </p>
            <p>
              Google Analytics ist ein Trackingtool, das der Datenverkehrsanalyse unserer Website dient. Damit Google Analytics funktioniert, wird ein
              Tracking-Code in den Code unserer Website eingebaut. Wenn Sie unsere Website besuchen, zeichnet dieser Code verschiedene Handlungen auf,
              die Sie auf unserer Website ausführen. Sobald Sie unsere Website verlassen, werden diese Daten an die Google-Analytics-Server gesendet
              und dort gespeichert.
            </p>
            <p>
              Google verarbeitet die Daten und wir bekommen Berichte über Ihr Userverhalten. Dabei kann es sich unter anderem um folgende Berichte
              handeln:
            </p>
            <ul>
              <li>
                Zielgruppenberichte: Über Zielgruppenberichte lernen wir unsere User besser kennen und wissen genauer, wer sich für unser Service
                interessiert.
              </li>
              <li>Anzeigeberichte: Durch Anzeigeberichte können wir unsere Onlinewerbung leichter analysieren und verbessern.</li>
              <li>
                Akquisitionsberichte: Akquisitionsberichte geben uns hilfreiche Informationen darüber, wie wir mehr Menschen für unser Service
                begeistern können.
              </li>
              <li>
                Verhaltensberichte: Hier erfahren wir, wie Sie mit unserer Website interagieren. Wir können nachvollziehen welchen Weg Sie auf unserer
                Seite zurücklegen und welche Links Sie anklicken.
              </li>
              <li>
                Conversionsberichte: Conversion nennt man einen Vorgang, bei dem Sie aufgrund einer Marketing-Botschaft eine gewünschte Handlung
                ausführen. Zum Beispiel, wenn Sie von einem reinen Websitebesucher zu einem Käufer oder Newsletter-Abonnent werden. Mithilfe dieser
                Berichte erfahren wir mehr darüber, wie unsere Marketing-Maßnahmen bei Ihnen ankommen. So wollen wir unsere Conversionrate steigern.
              </li>
              <li>
                Echtzeitberichte: Hier erfahren wir immer sofort, was gerade auf unserer Website passiert. Zum Beispiel sehen wir wie viele User
                gerade diesen Text lesen.
              </li>
            </ul>
            <h3>Warum verwenden wir Google Analytics auf unserer Website?</h3>
            <p>
              Unser Ziel mit dieser Website ist klar: Wir wollen Ihnen das bestmögliche Service bieten. Die Statistiken und Daten von Google Analytics
              helfen uns dieses Ziel zu erreichen.
            </p>
            <p>
              Die statistisch ausgewerteten Daten zeigen uns ein klares Bild von den Stärken und Schwächen unserer Website. Einerseits können wir
              unsere Seite so optimieren, dass sie von interessierten Menschen auf Google leichter gefunden wird. Andererseits helfen uns die Daten,
              Sie als Besucher besser zu verstehen. Wir wissen somit sehr genau, was wir an unserer Website verbessern müssen, um Ihnen das
              bestmögliche Service zu bieten. Die Daten dienen uns auch, unsere Werbe- und Marketing-Maßnahmen individueller und kostengünstiger
              durchzuführen. Schließlich macht es nur Sinn, unsere Produkte und Dienstleistungen Menschen zu zeigen, die sich dafür interessieren.
            </p>
            <h3>Welche Daten werden von Google Analytics gespeichert?</h3>
            <p>
              Google Analytics erstellt mithilfe eines Tracking-Codes eine zufällige, eindeutige ID, die mit Ihrem Browser-Cookie verbunden ist. So
              erkennt Sie Google Analytics als neuen User. Wenn Sie das nächste Mal unsere Seite besuchen, werden Sie als „wiederkehrender“ User
              erkannt. Alle gesammelten Daten werden gemeinsam mit dieser User-ID gespeichert. So ist es überhaupt erst möglich pseudonyme Userprofile
              auszuwerten.
            </p>
            <p>
              Um mit Google Analytics unsere Website analysieren zu können, muss eine Property-ID in den Tracking-Code eingefügt werden. Die Daten
              werden dann in der entsprechenden Property gespeichert. Für jede neu angelegte Property ist die Google Analytics 4-Property
              standardmäßig. Alternativ kann man aber auch noch die Universal Analytics Property erstellen. Je nach verwendeter Property werden Daten
              unterschiedlich lange gespeichert.
            </p>
            <p>
              Durch Kennzeichnungen wie Cookies und App-Instanz-IDs werden Ihre Interaktionen auf unserer Website gemessen. Interaktionen sind alle
              Arten von Handlungen, die Sie auf unserer Website ausführen. Wenn Sie auch andere Google-Systeme (wie z.B. ein Google-Konto) nützen,
              können über Google Analytics generierte Daten mit Drittanbieter-Cookies verknüpft werden. Google gibt keine Google Analytics-Daten
              weiter, außer wir als Websitebetreiber genehmigen das. Zu Ausnahmen kann es kommen, wenn es gesetzlich erforderlich ist.
            </p>
            <p>
              <strong>Name:</strong> __utmv
              <br />
              <strong>Wert:</strong> keine Angabe
              <br />
              <strong>Verwendungszweck:</strong> Das Cookie wird verwendet, um benutzerdefinierte Userdaten zu speichern. Es wird immer aktualisiert,
              wenn Informationen an Google Analytics gesendet werden.
              <br />
              <strong>Ablaufdatum:</strong> nach 2 Jahren
            </p>
            <p>
              <strong>Anmerkung:</strong>&nbsp;Diese Aufzählung kann keinen Anspruch auf Vollständigkeit erheben, da Google die Wahl ihrer Cookies
              immer wieder auch verändert.
            </p>
            <p>Hier zeigen wir Ihnen einen Überblick über die wichtigsten Daten, die mit Google Analytics erhoben werden:</p>
            <p>
              <strong>Heatmaps:</strong> Google legt sogenannte Heatmaps an. Über Heatmaps sieht man genau jene Bereiche, die Sie anklicken. So
              bekommen wir Informationen, wo Sie auf unserer Seite „unterwegs“ sind.
            </p>
            <p>
              <strong>Sitzungsdauer:</strong> Als Sitzungsdauer bezeichnet Google die Zeit, die Sie auf unserer Seite verbringen, ohne die Seite zu
              verlassen. Wenn Sie 20 Minuten inaktiv waren, endet die Sitzung automatisch.
            </p>
            <p>
              <strong>Absprungrate</strong> (engl. Bouncerate): Von einem Absprung ist die Rede, wenn Sie auf unserer Website nur eine Seite ansehen
              und dann unsere Website wieder verlassen.
            </p>
            <p>
              <strong>Kontoerstellung:</strong> Wenn Sie auf unserer Website ein Konto erstellen bzw. eine Bestellung machen, erhebt Google Analytics
              diese Daten.
            </p>
            <p>
              <strong>IP-Adresse:</strong> Die IP-Adresse wird nur in gekürzter Form dargestellt, damit keine eindeutige Zuordnung möglich ist.
            </p>
            <p>
              <strong>Standort:</strong> Über die IP-Adresse kann das Land und Ihr ungefährer Standort bestimmt werden. Diesen Vorgang bezeichnet man
              auch als IP- Standortbestimmung.
            </p>
            <p>
              <strong>Technische Informationen:</strong> Zu den technischen Informationen zählen unter anderem Ihr Browsertyp, Ihr Internetanbieter
              oder Ihre Bildschirmauflösung.
            </p>
            <p>
              <strong>Herkunftsquelle:</strong> Google Analytics beziehungsweise uns interessiert natürlich auch über welche Website oder welche
              Werbung Sie auf unsere Seite gekommen sind.
            </p>
            <p>
              Weitere Daten sind Kontaktdaten, etwaige Bewertungen, das Abspielen von Medien (z.B., wenn Sie ein Video über unsere Seite abspielen),
              das Teilen von Inhalten über Social Media oder das Hinzufügen zu Ihren Favoriten. Die Aufzählung hat keinen Vollständigkeitsanspruch und
              dient nur zu einer allgemeinen Orientierung der Datenspeicherung durch Google Analytics.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Google hat Ihre Server auf der ganzen Welt verteilt. Die meisten Server befinden sich in Amerika und folglich werden Ihre Daten meist
              auf amerikanischen Servern gespeichert. Hier können Sie genau nachlesen wo sich die Google-Rechenzentren befinden:
              <a href="https://www.google.com/about/datacenters/inside/locations/?hl=de" target="_blank" rel="noopener noreferrer">
                https://www.google.com/about/datacenters/inside/locations/?hl=de
              </a>
            </p>
            <p>
              Ihre Daten werden auf verschiedenen physischen Datenträgern verteilt. Das hat den Vorteil, dass die Daten schneller abrufbar sind und
              vor Manipulation besser geschützt sind. In jedem Google-Rechenzentrum gibt es entsprechende Notfallprogramme für Ihre Daten. Wenn
              beispielsweise die Hardware bei Google ausfällt oder Naturkatastrophen Server lahmlegen, bleibt das Risiko einer Dienstunterbrechung bei
              Google dennoch gering.
            </p>
            <p>
              Die Aufbewahrungsdauer der Daten hängt von den verwendeten Properties ab. Bei der Verwendung der neueren Google Analytics 4-Properties
              ist die Aufbewahrungsdauer Ihrer Userdaten auf 14 Monate fix eingestellt. Für andere sogenannte Ereignisdaten haben wir die Möglichkeit
              eine Aufbewahrungsdauer von 2 Monaten oder 14 Monaten zu wählen.
            </p>
            <p>
              Bei Universal Analytics-Properties ist bei Google Analytics eine Aufbewahrungsdauer Ihrer Userdaten von 26 Monaten standardisiert
              eingestellt. Dann werden Ihre Userdaten gelöscht. Allerdings haben wir die Möglichkeit, die Aufbewahrungsdauer von Nutzdaten selbst zu
              wählen. Dafür stehen uns fünf Varianten zur Verfügung:
            </p>
            <ul>
              <li>Löschung nach 14 Monaten</li>
              <li>Löschung nach 26 Monaten</li>
              <li>Löschung nach 38 Monaten</li>
              <li>Löschung nach 50 Monaten</li>
              <li>Keine automatische Löschung</li>
            </ul>
            <p>
              Zusätzlich gibt es auch die Option, dass Daten erst dann gelöscht werden, wenn Sie innerhalb des von uns gewählten Zeitraums nicht mehr
              unsere Website besuchen. In diesem Fall wird die Aufbewahrungsdauer jedes Mal zurückgesetzt, wenn Sie unsere Website innerhalb des
              festgelegten Zeitraums wieder besuchen.
            </p>
            <p>
              Wenn der festgelegte Zeitraum abgelaufen ist, werden einmal im Monat die Daten gelöscht. Diese Aufbewahrungsdauer gilt für Ihre Daten,
              die mit Cookies, Usererkennung und Werbe-IDs (z.B. Cookies der DoubleClick-Domain) verknüpft sind. Berichtergebnisse basieren auf
              aggregierten Daten und werden unabhängig von Nutzerdaten gespeichert. Aggregierte Daten sind eine Zusammenschmelzung von Einzeldaten zu
              einer größeren Einheit.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Nach dem Datenschutzrecht der Europäischen Union haben Sie das Recht, Auskunft über Ihre Daten zu erhalten, sie zu aktualisieren, zu
              löschen oder einzuschränken. Mithilfe des Browser-Add-ons zur Deaktivierung von Google Analytics-JavaScript (ga.js, analytics.js, dc.js)
              verhindern Sie, dass Google Analytics Ihre Daten verwendet. Das Browser-Add-on können Sie unter
              <a href="https://tools.google.com/dlpage/gaoptout?hl=de" target="_blank" rel="noopener noreferrer">
                https://tools.google.com/dlpage/gaoptout?hl=de
              </a>
              runterladen und installieren. Beachten Sie bitte, dass durch dieses Add-on nur die Datenerhebung durch Google Analytics deaktiviert
              wird.
            </p>
            <p>
              Falls Sie grundsätzlich Cookies (unabhängig von Google Analytics) deaktivieren, löschen oder verwalten wollen, gibt es für jeden Browser
              eine eigene Anleitung:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noopener noreferrer">
                Chrome: Cookies in Chrome löschen, aktivieren und verwalten
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/de-at/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noopener noreferrer">
                Safari: Verwalten von Cookies und Websitedaten mit Safari
              </a>
            </p>
            <p>
              <a
                href="https://support.mozilla.org/de/kb/cookies-und-website-daten-in-firefox-loschen?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firefox: Cookies löschen, um Daten zu entfernen, die Websites auf Ihrem Computer abgelegt haben
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Internet Explorer: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/4027947/windows-delete-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Edge: Löschen und Verwalten von Cookies
              </a>
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Der Einsatz von Google Analytics setzt Ihre Einwilligung voraus, welche wir mit unserem Cookie Popup eingeholt haben. Diese Einwilligung
              stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für die Verarbeitung personenbezogener Daten,
              wie sie bei der Erfassung durch Web-Analytics Tools vorkommen kann, dar.
            </p>
            <p>
              Zusätzlich zur Einwilligung besteht von unserer Seite ein berechtigtes Interesse daran, dass Verhalten der Websitebesucher zu
              analysieren und so unser Angebot technisch und wirtschaftlich zu verbessern. Mit Hilfe von Google Analytics erkennen wir Fehler der
              Website, können Attacken identifizieren und die Wirtschaftlichkeit verbessern. Die Rechtsgrundlage dafür ist
              <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>. Wir setzen Google Analytics gleichwohl nur ein, soweit Sie eine
              Einwilligung erteilt haben.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch Google. Dies kann dazu führen, dass gegebenenfalls Daten nicht
              anonymisiert verarbeitet und gespeichert werden. Ferner können gegebenenfalls US-amerikanische staatliche Behörden Zugriff auf einzelne
              Daten nehmen. Es kann ferner vorkommen, dass diese Daten mit Daten aus anderen Diensten von Google, bei denen Sie ein Nutzerkonto haben,
              verknüpft werden.
            </p>
            <p>
              Wir hoffen, wir konnten Ihnen die wichtigsten Informationen rund um die Datenverarbeitung von Google Analytics näherbringen. Wenn Sie
              mehr über den Tracking-Dienst erfahren wollen, empfehlen wir diese beiden Links:
              <a href="http://www.google.com/analytics/terms/de.html" target="_blank" rel="noopener noreferrer">
                http://www.google.com/analytics/terms/de.html
              </a>
              und
              <a href="https://support.google.com/analytics/answer/6004245?hl=de" target="_blank" rel="noopener noreferrer">
                https://support.google.com/analytics/answer/6004245?hl=de
              </a>
              .
            </p>
            <h2 id="google-analytics-ip-anonymisierung">Google Analytics IP-Anonymisierung</h2>
            <p>
              Wir haben auf dieser Webseite die IP-Adressen-Anonymisierung von Google Analytics implementiert.&nbsp;Diese Funktion wurde von Google
              entwickelt, damit diese Webseite die geltenden Datenschutzbestimmungen und Empfehlungen der lokalen Datenschutzbehörden einhalten kann,
              wenn diese eine Speicherung der vollständigen IP-Adresse untersagen. Die Anonymisierung bzw. Maskierung der IP findet statt, sobald die
              IP-Adressen im Google Analytics-Datenerfassungsnetzwerk eintreffen und bevor eine Speicherung oder Verarbeitung der Daten stattfindet.
            </p>
            <p>
              Mehr Informationen zur IP-Anonymisierung finden Sie auf&nbsp;
              <a href="https://support.google.com/analytics/answer/2763052?hl=de" target="_blank" rel="noreferrer">
                https://support.google.com/analytics/answer/2763052?hl=de
              </a>
              .
            </p>
            <h2 id="google-tag-manager-datenschutzerklaerung">Google Tag Manager Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>Google Tag Manager Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Organisation der einzelnen Tracking-Tools</li>
              <li>
                Verarbeitete Daten: Der Google Tag Manager speichert selbst keine Daten. Die Daten erfassen die Tags der eingesetzten
                Web-Analytics-Tools.
              </li>
              <li>Speicherdauer: abhängig vom eingesetzten Web-Analytics-Tool</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist der Google Tag Manager?</h3>
            <p>
              Für unsere Website verwenden wir den Google Tag Manager des Unternehmens Google Inc. Für den europäischen Raum ist das Unternehmen
              Google Ireland Limited (Gordon House, Barrow Street Dublin 4, Irland) für alle Google-Dienste verantwortlich. Dieser Tag Manager ist
              eines von vielen hilfreichen Marketing-Produkten von Google. Über den Google Tag Manager können wir Code-Abschnitte von diversen
              Trackingtools, die wir auf unserer Webseite verwenden, zentral einbauen und verwalten.
            </p>
            <p>
              In dieser Datenschutzerklärung wollen wir Ihnen genauer erklären was der Google Tag Manager macht, warum wir ihn verwenden und in
              welcher Form Daten verarbeitet werden.
            </p>
            <p>
              Der Google Tag Manager ist ein Organisationstool, mit dem wir Webseiten-Tags zentral und über eine Benutzeroberfläche einbinden und
              verwalten können. Als Tags bezeichnet man kleine Code-Abschnitte, die beispielsweise Ihre Aktivitäten auf unserer Webseite aufzeichnen
              (tracken). Dafür werden JavaScript-Code-Abschnitte in den Quelltext unserer Seite eingesetzt. Die Tags stammen oft von google-internen
              Produkten wie Google Ads oder Google Analytics, aber auch Tags von anderen Unternehmen können über den Manager eingebunden und verwaltet
              werden. Solche Tags übernehmen unterschiedliche Aufgaben. Sie können Browserdaten sammeln, Marketingtools mit Daten füttern, Buttons
              einbinden, Cookies setzen und auch Nutzer über mehrere Webseiten hinweg verfolgen.
            </p>
            <h3>Warum verwenden wir den Google Tag Manager für unserer Website?</h3>
            <p>
              Wie sagt man so schön: Organisation ist die halbe Miete! Und das betrifft natürlich auch die Pflege unserer Webseite. Um unsere Webseite
              für Sie und alle Menschen, die sich für unsere Produkte und Dienstleistungen interessieren, so gut wie möglich zu gestalten, brauchen
              wir diverse Trackingtools wie beispielsweise Google Analytics. Die erhobenen Daten dieser Tools zeigen uns, was Sie am meisten
              interessiert, wo wir unsere Leistungen verbessern können und welchen Menschen wir unsere Angebote noch zeigen sollten. Und damit dieses
              Tracking funktioniert, müssen wir entsprechende JavaScript-Codes in unsere Webseite einbinden. Grundsätzlich könnten wir jeden
              Code-Abschnitt der einzelnen Tracking-Tools separat in unseren Quelltext einbauen. Das erfordert allerdings relativ viel Zeit und man
              verliert leicht den Überblick. Darum nützen wir den Google Tag Manager. Wir können die nötigen Skripte einfach einbauen und von einem
              Ort aus verwalten. Zudem bietet der Google Tag Manager eine leicht zu bedienende Benutzeroberfläche und man benötigt keine
              Programmierkenntnisse. So schaffen wir es, Ordnung in unserem Tag-Dschungel zu halten.
            </p>
            <h3>Welche Daten werden vom Google Tag Manager gespeichert?</h3>
            <p>
              Der Tag Manager selbst ist eine Domain, die keine Cookies setzt und keine Daten speichert. Er fungiert als bloßer „Verwalter“ der
              implementierten Tags. Die Daten erfassen die einzelnen Tags der unterschiedlichen Web-Analysetools. Die Daten werden im Google Tag
              Manager quasi zu den einzelnen Tracking-Tools durchgeschleust und nicht gespeichert.
            </p>
            <p>
              Ganz anders sieht das allerdings mit den eingebundenen Tags der verschiedenen Web-Analysetools, wie zum Beispiel Google Analytics, aus.
              Je nach Analysetool werden meist mit Hilfe von Cookies verschiedene Daten über Ihr Webverhalten gesammelt, gespeichert und verarbeitet.
              Dafür lesen Sie bitte unsere Datenschutztexte zu den einzelnen Analyse- und Trackingtools, die wir auf unserer Webseite verwenden.
            </p>
            <p>
              In den Kontoeinstellungen des Tag Managers haben wir Google erlaubt, dass Google anonymisierte Daten von uns erhält. Dabei handelt es
              sich aber nur um die Verwendung und Nutzung unseres Tag Managers und nicht um Ihre Daten, die über die Code-Abschnitte gespeichert
              werden. Wir ermöglichen Google und anderen, ausgewählte Daten in anonymisierter Form zu erhalten. Wir stimmen somit der anonymen
              Weitergabe unseren Website-Daten zu. Welche zusammengefassten und anonymen Daten genau weitergeleitet werden, konnten wir – trotz langer
              Recherche – nicht in Erfahrung bringen. Auf jeden Fall löscht Google dabei alle Infos, die unsere Webseite identifizieren könnten.
              Google fasst die Daten mit Hunderten anderen anonymen Webseiten-Daten zusammen und erstellt, im Rahmen von Benchmarking-Maßnahmen,
              Usertrends. Bei Benchmarking werden eigene Ergebnisse mit jenen der Mitbewerber verglichen. Auf Basis der erhobenen Informationen können
              Prozesse optimiert werden.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Wenn Google Daten speichert, dann werden diese Daten auf den eigenen Google-Servern gespeichert. Die Server sind auf der ganzen Welt
              verteilt. Die meisten befinden sich in Amerika. Unter
              <a href="https://www.google.com/about/datacenters/inside/locations/?hl=de" target="_blank" rel="noopener noreferrer">
                https://www.google.com/about/datacenters/inside/locations/?hl=de
              </a>
              können Sie genau nachlesen, wo sich die Google-Server befinden.
            </p>
            <p>
              Wie lange die einzelnen Tracking-Tools Daten von Ihnen speichern, entnehmen Sie unseren individuellen Datenschutztexten zu den einzelnen
              Tools.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Der Google Tag Manager selbst setzt keine Cookies, sondern verwaltet Tags verschiedener Tracking-Webseiten. In unseren Datenschutztexten
              zu den einzelnen Tracking-Tools, finden Sie detaillierte Informationen wie Sie Ihre Daten löschen bzw. verwalten können.
            </p>
            <p>
              Bitte beachten Sie, dass bei der Verwendung dieses Tools Daten von Ihnen auch außerhalb der EU gespeichert und verarbeitet werden
              können. Die meisten Drittstaaten (darunter auch die USA) gelten nach derzeitigem europäischen Datenschutzrecht als nicht sicher. Daten
              an unsichere Drittstaaten dürfen also nicht einfach übertragen, dort gespeichert und verarbeitet werden, sofern es keine passenden
              Garantien (wie etwa EU-Standardvertragsklauseln) zwischen uns und dem außereuropäischen Dienstleister gibt.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Der Einsatz des Google Tag Managers setzt Ihre Einwilligung voraus, welche wir mit unserem Cookie Popup eingeholt haben. Diese
              Einwilligung stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für die Verarbeitung
              personenbezogener Daten, wie sie bei der Erfassung durch Web-Analytics Tools vorkommen kann, dar.
            </p>
            <p>
              Zusätzlich zur Einwilligung besteht von unserer Seite ein berechtigtes Interesse daran, dass Verhalten der Websitebesucher zu
              analysieren und so unser Angebot technisch und wirtschaftlich zu verbessern. Mit Hilfe der Google Tag Managers können die
              Wirtschaftlichkeit verbessern. Die Rechtsgrundlage dafür ist <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>. Wir
              setzen den Google Tag Manager gleichwohl nur ein, soweit Sie eine Einwilligung erteilt haben.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch Google (sofern beispielsweise Google Analytics im Google Tag
              Manager verwendet wird). Dies kann dazu führen, dass gegebenenfalls Daten nicht anonymisiert verarbeitet und gespeichert werden. Ferner
              können gegebenenfalls US-amerikanische staatliche Behörden Zugriff auf einzelne Daten nehmen. Es kann ferner vorkommen, dass diese Daten
              mit Daten aus anderen Diensten von Google, bei denen Sie ein Nutzerkonto haben, verknüpft werden.
            </p>
            <p>
              Wenn Sie mehr über den Google Tag Manager erfahren wollen, empfehlen wir Ihnen die FAQs unter
              <a href="https://www.google.com/intl/de/tagmanager/faq.html" target="_blank" rel="noopener noreferrer">
                https://www.google.com/intl/de/tagmanager/faq.html
              </a>
              .
            </p>
            <p>&nbsp;</p>
            <h2 id="e-mail-marketing">E-Mail-Marketing</h2>
            <ul>
              <li>
                <strong>E-Mail-Marketing Zusammenfassung</strong>
              </li>
              <li>Betroffene: Newsletter-Abonnenten</li>
              <li>Zweck: Direktwerbung per E-Mail, Benachrichtigung über systemrelevante Ereignisse</li>
              <li>
                Verarbeitete Daten: Eingegebene Daten bei der Registrierung jedoch mindestens die E-Mail-Adresse. Mehr Details dazu finden Sie beim
                jeweils eingesetzten E-Mail-Marketing-Tool.
              </li>
              <li>Speicherdauer: Dauer des Bestehens des Abonnements</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)</li>
            </ul>
            <h3>Was ist E-Mail-Marketing?</h3>
            <p>
              Um Sie stets auf dem Laufenden zu halten, nutzen wir auch die Möglichkeit des E-Mail-Marketings. Dabei werden, sofern Sie dem Empfang
              unserer E-Mails bzw. Newsletter zugestimmt haben, auch Daten von Ihnen verarbeitet und gespeichert. E-Mail-Marketing ist ein Teilbereich
              des Online-Marketings. Dabei werden Neuigkeiten oder allgemeine Informationen über ein Unternehmen, Produkte oder Dienstleistungen per
              E-Mail an eine bestimmte Gruppe an Menschen, die sich dafür interessieren, gesendet.
            </p>
            <p>
              Wenn Sie an unserem E-Mail-Marketing (meist per Newsletter) teilnehmen wollen, müssen Sie sich im Normalfall einfach nur mit Ihrer
              E-Mail-Adresse anmelden. Dafür füllen Sie ein Online-Formular aus und senden es ab. Es kann aber auch vorkommen, dass wir Sie etwa um
              Ihre Anrede und Ihren Namen bitten, damit wir Sie auch persönlich anschreiben können.
            </p>
            <p>
              Grundsätzlich funktioniert das Anmelden zu Newslettern mit Hilfe des sogenannten „Double-Opt-In-Verfahrens“. Nachdem Sie sich für
              unseren Newsletter auf unserer Website angemeldet haben, bekommen Sie eine E-Mail, über die Sie die Newsletter-Anmeldung bestätigen. So
              wird sichergestellt, dass Ihnen die E-Mail-Adresse gehört und sich niemand mit einer fremden E-Mail-Adresse angemeldet hat. Wir oder ein
              von uns verwendetes Benachrichtigungs-Tool protokolliert jede einzelne Anmeldung. Dies ist nötig, damit wir den rechtlich korrekten
              Anmeldevorgang auch nachweisen können. Dabei wird in der Regel der Zeitpunkt der Anmeldung, der Zeitpunkt der Anmeldebestätigung und
              Ihre IP-Adresse gespeichert. Zusätzlich wird auch protokolliert, wenn Sie Änderungen Ihrer gespeicherten Daten vornehmen.
            </p>
            <h3>Warum nutzen wir E-Mail-Marketing?</h3>
            <p>
              Wir wollen natürlich mit Ihnen in Kontakt bleiben und Ihnen stets die wichtigsten Neuigkeiten über unser Unternehmen präsentieren. Dafür
              nutzen wir unter anderem E-Mail-Marketing – oft auch nur “Newsletter” bezeichnet – als wesentlichen Bestandteil unseres
              Online-Marketings. Sofern Sie sich damit einverstanden erklären oder es gesetzlich erlaubt ist, schicken wir Ihnen Newsletter,
              System-E-Mails oder andere Benachrichtigungen per E-Mail. Wenn wir im folgenden Text den Begriff „Newsletter“ verwenden, meinen wir
              damit hauptsächlich regelmäßig versandte E-Mails. Natürlich wollen wir Sie mit unseren Newsletter in keiner Weise belästigen. Darum sind
              wir wirklich stets bemüht, nur relevante und interessante Inhalte zu bieten. So erfahren Sie etwa mehr über unser Unternehmen, unsere
              Leistungen oder Produkte. Da wir unsere Angebote auch immer verbessern, erfahren Sie über unseren Newsletter auch immer, wenn es
              Neuigkeiten gibt oder wir gerade spezielle, lukrative Aktionen anbieten. Sofern wir einen Dienstleister, der ein professionelles
              Versand-Tool anbietet, für unser E-Mail-Marketing beauftragen, machen wir das, um Ihnen schnelle und sichere Newsletter bieten zu
              können. Zweck unseres E-Mail-Marketings ist grundsätzlich, Sie über neue Angebote zu informieren und auch unseren unternehmerischen
              Zielen näher zu kommen.
            </p>
            <h3>Welche Daten werden verarbeitet?</h3>
            <p>
              Wenn Sie über unsere Website Abonnent unseres Newsletters werden, bestätigen Sie per E-Mail die Mitgliedschaft in einer E-Mail-Liste.
              Neben IP-Adresse und E-Mail-Adresse können auch Ihre Anrede, Ihr Name, Ihre Adresse und Ihre Telefonnummer gespeichert werden.
              Allerdings nur, wenn Sie dieser Datenspeicherungen zustimmen. Die als solche markierten Daten sind notwendig, damit Sie an dem
              angebotenen Dienst teilnehmen können. Die Angabe ist freiwillig, die Nichtangabe führt jedoch dazu, dass Sie den Dienst nicht nutzen
              können. Zusätzlich können etwa auch Informationen zu Ihrem Gerät oder zu Ihren bevorzugten Inhalten auf unserer Website gespeichert
              werden. Mehr zur Speicherung von Daten, wenn Sie eine Website besuchen, finden Sie im Abschnitt “Automatische Datenspeicherung”. Ihre
              Einwilligungserklärung zeichnen wir auf, damit wir stets nachweisen können, dass dieser unseren Gesetzen entspricht.
            </p>
            <h3>Dauer der Datenverarbeitung</h3>
            <p>
              Wenn Sie Ihre E-Mail-Adresse aus unserem E-Mail/Newsletter-Verteiler austragen, dürfen wir Ihre Adresse bis zu drei Jahren auf Grundlage
              unserer berechtigten Interessen speichern, damit wir Ihre damalige Einwilligung noch nachweisen können. Verarbeiten dürfen wir diese
              Daten nur, wenn wir uns gegen etwaige Ansprüche wehren müssen.
            </p>
            <p>
              Wenn Sie allerdings bestätigen, dass Sie uns die Einwilligung zur Newsletter-Anmeldung gegeben haben, können Sie jederzeit einen
              individuellen Löschantrag stellen. Widersprechen Sie der Einwilligung dauerhaft, behalten wir uns das Recht vor, Ihre E-Mail-Adresse in
              einer Sperrliste zu speichern. Solange Sie freiwillig unseren Newsletter abonniert haben, solange behalten wir selbstverständlich auch
              Ihre E-Mail-Adresse.
            </p>
            <h3>Widerruf – wie kann ich mein Abo kündigen?</h3>
            <p>
              Sie haben jederzeit die Möglichkeit Ihre Newsletter-Anmeldung zu kündigen. Dafür müssen Sie lediglich Ihre Einwilligung zur
              Newsletter-Anmeldung widerrufen. Das dauert im Normalfall nur wenige Sekunden bzw. einen oder zwei Klicks. Meistens finden Sie direkt am
              Ende jeder E-Mail einen Link, um das Newsletter-Abonnement zu kündigen. Wenn der Link im Newsletter wirklich nicht zu finden ist,
              kontaktieren Sie uns bitte per Mail und wir werden Ihr Newsletter-Abo unverzüglich kündigen.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Das Versenden unseres Newsletters erfolgt auf Grundlage Ihrer <strong>Einwilligung</strong> (Artikel 6 Abs. 1 lit. a DSGVO). Das heißt,
              wir dürfen Ihnen nur dann einen Newsletter schicken, wenn Sie sich zuvor aktiv dafür angemeldet haben. Gegebenenfalls können wir Ihnen
              auch Werbenachrichten auf Grundlage des § 7 Abs. 3 UWG senden, sofern Sie unser Kunde geworden sind und der Verwendung ihrer
              E-Mailadresse für Direktwerbung nicht widersprochen haben.
            </p>
            <p>
              Informationen zu speziellen E-Mail-Marketing Diensten und wie diese personenbezogene Daten verarbeiten, erfahren Sie – sofern vorhanden
              – in den folgenden Abschnitten.
            </p>
            <h2 id="mailchimp-datenschutzerklaerung">MailChimp Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>MailChimp Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Newsletter-Abonnenten</li>
              <li>Zweck: Direktwerbung per E-Mail, Benachrichtigung über systemrelevante Ereignisse</li>
              <li>Verarbeitete Daten: Eingegebene Daten bei der Registrierung jedoch mindestens die E-Mail-Adresse.</li>
              <li>Speicherdauer: Dauer des Bestehens des Abonnements</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (berechtigte Interessen)</li>
            </ul>
            <h3>Was ist MailChimp?</h3>
            <p>
              Wie viele andere Webseiten verwenden auch wir auf unserer Website die Dienste des Newsletter-Unternehmens MailChimp. Der Betreiber von
              MailChimp ist das Unternehmen The Rocket Science Group, LLC, 675 Ponce de Leon Ave NE, Suite 5000, Atlanta, GA 30308 USA. Dank MailChimp
              können wir Ihnen interessante Neuigkeiten sehr einfach per Newsletter zukommen lassen. Mit MailChimp müssen wir nichts installieren und
              können trotzdem aus einem Pool an wirklich nützlichen Funktionen schöpfen. Im Folgenden gehen wir näher auf dieses
              E-Mail-Marketing-Service ein und informieren Sie über die wichtigsten datenschutzrelevanten Aspekte.
            </p>
            <p>
              MailChimp ist ein cloudbasiertes Newsletter-Management-Service. „Cloudbasiert“ heißt, dass wir MailChimp nicht auf unserem eigenen
              Rechner bzw. Server installieren müssen. Wir nutzen den Dienst statulessen über eine IT-Infrastruktur – die über das Internet verfügbar
              ist – auf einem externen Server. Diese Art eine Software zu nutzen, wird auch SaaS (Software as a Service) genannt. Die folgende Grafik
              zeigt schematisch, wie MailChimp E-Mails an Newsletter Empfänger verteilt.
            </p>
            <p>
              <img src="https://www.adsimple.at/wp-content/uploads/2018/05/mailchimp-overview.svg" alt="Systematische Funktion von mailchimp" />
            </p>
            <p>
              Mit MailChimp können wir aus einer breiten Palette an verschiedenen E-Mail-Typen auswählen. Abhängig davon, was wir mit unserem
              Newsletter erreichen wollen, können wir Einzel-Kampagnen, regelmäßige Kampagnen, Autoresponder (automatische E-Mails), A/B Tests,
              RSS-Kampagnen (Aussendung in vordefinierter Zeit und Häufigkeit) und Follow-Up Kampagnen durchführen.
            </p>
            <h3>Warum verwenden wir MailChimp auf unserer Website?</h3>
            <p>
              Grundsätzlich nutzen wir einen Newsletter-Dienst, damit wir mit Ihnen in Kontakt bleiben. Wir wollen Ihnen erzählen was es bei uns Neues
              gibt oder welche attraktiven Angebote wir gerade in unserem Programm haben. Für unsere Marketing-Maßnahmen suchen wir immer die
              einfachsten und besten Lösungen. Und aus diesem Grund haben wir uns auch für das Newsletter-Management-Service von MailChimp
              entschieden. Obwohl die Software sehr einfach zu bedienen ist, bietet sie eine große Anzahl an hilfreichen Features. So können wir in
              nur kurzer Zeit interessante und schöne Newsletter gestalten. Durch die angebotenen Designvorlagen gestalten wir jeden Newsletter ganz
              individuell und dank des „Responsive Design“ werden unsere Inhalte auch auf Ihrem Smartphone (oder einem anderen mobilen Endgeräten)
              leserlich und schön angezeigt.
            </p>
            <p>
              Durch Tools wie beispielsweise den A/B-Test oder den umfangreichen Analysemöglichkeiten, sehen wir sehr schnell, wie unsere Newsletter
              bei Ihnen ankommen. So können wir gegebenenfalls reagieren und unser Angebot oder unsere Dienstleistungen verbessern.
            </p>
            <p>
              Ein weiterer Vorteil ist das „Cloudsystem“ von MailChimp. Die Daten werden nicht direkt auf unserem Server abgelegt und verarbeitet. Wir
              können die Daten von externen Servern abrufen und schonen auf diese Weise unseren Speicherplatz. Zudem wird der Pflegeaufwand deutlich
              geringer.
            </p>
            <h3>Welche Daten werden von MailChimp gespeichert?</h3>
            <p>
              Die Rocket Science Group LLC (MailChimp) unterhält Online-Plattformen, die es uns ermöglichen, mit Ihnen (sofern Sie unseren Newsletter
              abonniert haben) in Kontakt zu treten. Wenn Sie über unsere Website Abonnent unseres Newsletters werden, bestätigen Sie per E-Mail die
              Mitgliedschaft in einer E-Mail-Liste von MailChimp. Damit MailChimp auch nachweisen kann, dass Sie sich in den „Listenprovider“
              eingetragen haben, werden das Datum der Eintragung und Ihre IP-Adresse gespeichert. Weiters speichert MailChimp Ihre E-Mail-Adresse,
              Ihren Namen, die physische Adresse und demografische Informationen, wie Sprache oder Standort.
            </p>
            <p>
              Diese Informationen werden verwendet, um Ihnen E-Mails zu senden und bestimmte andere MailChimp-Funktionen (wie z.B. Auswertung der
              Newsletter) zu ermöglichen.
            </p>
            <p>
              MailChimp teilt Informationen auch mit Drittanbietern, um bessere Dienste bereitzustellen. Einige Daten teilt MailChimp auch mit
              Werbepartnern von Drittanbietern, um die Interessen und Anliegen seiner Kunden besser zu verstehen, damit relevantere Inhalte und
              zielgerichtete Werbung bereitgestellt werden können.
            </p>
            <p>
              Durch sogenannte „Web Beacons“ (das sind kleine Grafiken in HTML-E-Mails) kann MailChimp feststellen, ob die E-Mail angekommen ist, ob
              sie geöffnet wurde und ob Links angeklickt wurden. All diese Informationen werden auf den MailChimp-Servern gespeichert. Dadurch
              erhalten wir statistische Auswertungen und sehen genau, wie gut unser Newsletter bei Ihnen ankam. Auf diese Weise können wir unser
              Angebot viel besser an Ihre Wünsche anpassen und unser Service verbessern.
            </p>
            <p>
              MailChimp darf zudem diese Daten auch zur Verbesserung des eigenen Service-Dienstes verwenden. Dadurch kann beispielsweise der Versand
              technisch optimiert werden oder der Standort (das Land) der Empfänger bestimmt werden.
            </p>
            <p>
              Manchmal kann es vorkommen, dass Sie unseren Newsletter zur besseren Darstellung über einen angegebenen Link öffnen. Das ist zum
              Beispiel der Fall, wenn Ihr E-Mail-Programm nicht funktioniert oder der Newsletter nicht ordnungsgemäß anzeigt wird. Der Newsletter wir
              dann über eine Website von MailChimp angezeigt. MailChimp verwendet auf seinen eigenen Webseiten auch Cookies (kleine Text-Dateien, die
              Daten auf Ihrem Browser speichern). Dabei können personenbezogenen Daten durch MailChimp und dessen Partner (z.B. Google Analytics)
              verarbeitet werden. Diese Datenerhebung liegt in der Verantwortung von MailChimp und wir haben darauf keinen Einfluss. Im „Cookie
              Statement“ von MailChimp (unter:
              <a href="https://mailchimp.com/legal/cookies/" target="_blank" rel="noopener noreferrer">
                https://mailchimp.com/legal/cookies/
              </a>
              ) erfahren Sie genau, wie und warum das Unternehmen Cookies verwendet.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>Da MailChimp ein amerikanisches Unternehmen ist, werden alle gesammelten Daten auch auf amerikanischen Servern gespeichert.</p>
            <p>
              Grundsätzlich bleiben die Daten auf den Servern von MailChimp dauerhaft gespeichert und werden erst gelöscht, wenn eine Aufforderung von
              Ihnen erfolgt. Sie können Ihren Kontakt bei uns löschen lassen. Das entfernt für uns dauerhaft all Ihre persönlichen Daten und
              anonymisiert Sie in den MailChimp-Berichten. Sie können allerdings auch direkt bei MailChimp die Löschung Ihrer Daten anfordern. Dann
              werden dort all Ihre Daten entfernt und wir bekommen eine Benachrichtigung von MailChimp. Nachdem wir die E-Mail erhalten haben, haben
              wir 30 Tage Zeit, um Ihren Kontakt von allen verbundenen Integrationen zu löschen.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Sie können Ihre Zustimmung für den Erhalt unseres Newsletters jederzeit innerhalb der empfangenen E-Mail per Klick auf den Link im
              unteren Bereich entziehen. Wenn Sie sich mit einem Klick auf den Abmeldelink abgemeldet haben, werden Ihre Daten bei MailChimp gelöscht.
            </p>
            <p>
              Falls Sie über einen Link in unserem Newsletter auf eine Website von MailChimp gelangen und Cookies in Ihrem Browser gesetzt werden,
              können Sie diese Cookies jederzeit löschen oder deaktivieren.
            </p>
            <p>
              Je nach Browser funktioniert das Deaktivieren bzw. Löschen etwas anders. Die folgenden Anleitungen zeigen, wie Sie Cookies in Ihrem
              Browser verwalten:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noopener noreferrer">
                Chrome: Cookies in Chrome löschen, aktivieren und verwalten
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/de-at/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noopener noreferrer">
                Safari: Verwalten von Cookies und Websitedaten mit Safari
              </a>
            </p>
            <p>
              <a
                href="https://support.mozilla.org/de/kb/cookies-und-website-daten-in-firefox-loschen?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firefox: Cookies löschen, um Daten zu entfernen, die Websites auf Ihrem Computer abgelegt haben
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Internet Explorer: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/4027947/windows-delete-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Edge: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              Falls Sie grundsätzlich keine Cookies haben wollen, können Sie Ihren Browser so einrichten, dass er Sie immer informiert, wenn ein
              Cookie gesetzt werden soll. So können Sie bei jedem einzelnen Cookie entscheiden, ob Sie es erlauben oder nicht.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Das Versenden unseres Newsletters durch MailChimp erfolgt auf Grundlage Ihrer <strong>Einwilligung</strong> (Artikel 6 Abs. 1 lit. a
              DSGVO). Das heißt, wir dürfen Ihnen nur dann einen Newsletter schicken, wenn Sie sich zuvor aktiv dafür angemeldet haben. Wenn eine
              Einwilligung nicht nötig ist, dann erfolgt der Newsletter-Versand auf Grundlage des <strong>berechtigten Interesses</strong> am
              Direktmarketing (Artikel 6 Abs. 1 lit. f), sofern dies rechtlich erlaubt ist. Ihren Registrierungsprozess zeichnen wir auf, damit wir
              stets nachweisen können, dass dieser unseren Gesetzen entspricht.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch MailChimp. Dies kann dazu führen, dass gegebenenfalls Daten nicht
              anonymisiert verarbeitet und gespeichert werden. Ferner können gegebenenfalls US-amerikanische staatliche Behörden Zugriff auf einzelne
              Daten nehmen. Es kann ferner vorkommen, dass diese Daten mit Daten aus möglichen anderen Diensten von MailChimp, bei denen Sie ein
              Nutzerkonto haben, verknüpft werden.
            </p>
            <p>
              Mehr über den Einsatz von Cookies bei MailChimp erfahren Sie auf
              <a href="https://mailchimp.com/legal/cookies/" target="_blank" rel="noopener noreferrer">
                https://mailchimp.com/legal/cookies/
              </a>
              , Informationen zum Datenschutz bei MailChimp (Privacy) können Sie auf&nbsp;
              <a href="https://mailchimp.com/legal/privacy/" target="_blank" rel="noopener noreferrer">
                https://mailchimp.com/legal/privacy/
              </a>
              nachlesen.
            </p>
            <h2 id="openstreetmap-datenschutzerklaerung">OpenStreetMap Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>OpenStreetMap Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Optimierung unserer Serviceleistung</li>
              <li>
                Verarbeitete Daten: Daten wie etwa IP-Adresse, Browserinformationen, Ihr Betriebssystem, Inhalt der Anfrage, eingeschränkte Standorts-
                und Nutzungsdaten
              </li>
              <li>Mehr Details dazu finden Sie weiter unten in dieser Datenschutzerklärung.</li>
              <li>Speicherdauer: die IP-Adresse wird nach 180 Tagen gelöscht</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist OpenStreetMap?</h3>
            <p>
              Wir haben auf unserer Website Kartenausschnitte des Online-Kartentools „OpenStreetMap“ eingebunden. Dabei handelt es sich um ein
              sogenanntes Open-Source-Mapping, welches wir über eine API (Schnittstelle) abrufen können. Angeboten wird diese Funktion von
              OpenStreetMap Foundation, St John’s Innovation Centre, Cowley Road, Cambridge, CB4 0WS, United Kingdom. Durch die Verwendung dieser
              Kartenfunktion wird Ihre IP-Adresse an OpenStreetMap weitergeleitet. In dieser Datenschutzerklärung erfahren Sie warum wir Funktionen
              des Tools OpenStreetMap verwenden, wo welche Daten gespeichert werden und wie Sie diese Datenspeicherung verhindern können.
            </p>
            <p>
              Das Projekt OpenStreetMap wurde 2004 ins Leben gerufen. Ziel des Projekts ist und war es, eine freie Weltkarte zu erschaffen. User
              sammeln weltweit Daten etwa über Gebäude, Wälder, Flüsse und Straßen. So entstand über die Jahre eine umfangreiche, von Usern selbst
              erstellte digitale Weltkarte. Selbstverständlich ist die Karte, nicht vollständig, aber in den meisten Regionen mit sehr vielen Daten
              ausgestattet.
            </p>
            <h3>Warum verwenden wir OpenStreetMap auf unserer Website?</h3>
            <p>
              Unsere Website soll Ihnen in erster Linie hilfreich sein. Und das ist sie aus unserer Sicht immer dann, wenn man Information schnell und
              einfach findet. Da geht es natürlich einerseits um unsere Dienstleistungen und Produkte, andererseits sollen Ihnen auch weitere
              hilfreiche Informationen zur Verfügung stehen. Deshalb nutzen wir auch den Kartendienst OpenStreetMap. Denn so können wir Ihnen
              beispielsweise genau zeigen, wie Sie unsere Firma finden. Die Karte zeigt Ihnen den besten Weg zu uns und Ihre Anfahrt wird zum
              Kinderspiel.
            </p>
            <h3>Welche Daten werden von OpenStreetMap gespeichert?</h3>
            <p>
              Wenn Sie eine unserer Webseiten besuchen, die OpenStreetMap anbietet, werden Nutzerdaten an den Dienst übermittelt und dort gespeichert.
              OpenStreetMap sammelt etwa Informationen über Ihre Interaktionen mit der digitalen Karte, Ihre IP-Adresse, Daten zu Ihrem Browser,
              Gerätetyp, Betriebssystem und an welchem Tag und zu welcher Uhrzeit Sie den Dienst in Anspruch genommen haben. Dafür wird auch
              Tracking-Software zur Aufzeichnung von Userinteraktionen verwendet. Das Unternehmen gibt hier in der eigenen Datenschutzerklärung das
              Analysetool „Piwik“ an.
            </p>
            <p>
              Die erhobenen Daten sind in Folge den entsprechenden Arbeitsgruppen der OpenStreetMap Foundation zugänglich. Laut dem Unternehmen werden
              persönliche Daten nicht an andere Personen oder Firmen weitergegeben, außer dies ist rechtlich notwendig. Der Drittanbieter Piwik
              speichert zwar Ihre IP-Adresse, allerdings in gekürzter Form.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Die API-Server, die Datenbanken und die Server von Hilfsdiensten befinden sich derzeit im Vereinten Königreich (Großbritannien und
              Nordirland) und in den Niederlanden. Ihre IP-Adresse und Userinformationen, die in gekürzter Form durch das Webanalysetool Piwik
              gespeichert werden, werden nach 180 Tagen wieder gelöscht.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Sie haben jederzeit das Recht auf Ihre personenbezogenen Daten zuzugreifen und Einspruch gegen die Nutzung und Verarbeitung zu erheben.
              Cookies, die von OpenStreetMap möglicherweise gesetzt werden, können Sie in Ihrem Browser jederzeit verwalten, löschen oder
              deaktivieren. Dadurch wird allerdings der Dienst nicht mehr im vollen Ausmaß funktionieren. Bei jedem Browser funktioniert die
              Verwaltung, Löschung oder Deaktivierung von Cookies etwas anders. Im Folgenden finden Sie Links zu den Anleitungen der bekanntesten
              Browser:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noopener noreferrer">
                Chrome: Cookies in Chrome löschen, aktivieren und verwalten
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/de-at/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noopener noreferrer">
                Safari: Verwalten von Cookies und Websitedaten mit Safari
              </a>
            </p>
            <p>
              <a
                href="https://support.mozilla.org/de/kb/cookies-und-website-daten-in-firefox-loschen?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Firefox: Cookies löschen, um Daten zu entfernen, die Websites auf Ihrem Computer abgelegt haben
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Internet Explorer: Löschen und Verwalten von Cookies
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/de-at/help/4027947/windows-delete-cookies?tid=111779921"
                target="_blank"
                rel="noopener noreferrer"
              >
                Microsoft Edge: Löschen und Verwalten von Cookies
              </a>
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Wenn Sie eingewilligt haben, dass OpenStreetMap eingesetzt werden darf, ist die Rechtsgrundlage der entsprechenden Datenverarbeitung
              diese Einwilligung. Diese Einwilligung stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für
              die Verarbeitung personenbezogener Daten, wie sie bei der Erfassung durch OpenStreetMap vorkommen kann, dar.
            </p>
            <p>
              Von unserer Seite besteht zudem ein berechtigtes Interesse, OpenStreetMap zu verwenden, um unser Online-Service zu optimieren. Die dafür
              entsprechende Rechtsgrundlage ist <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>. Wir setzen OpenStreetMap
              gleichwohl nur ein, soweit Sie eine Einwilligung erteilt haben.
            </p>
            <p>
              Wenn Sie mehr über die Datenverarbeitung durch OpenStreetMap erfahren wollen, empfehlen wir Ihnen die Datenschutzerklärung des
              Unternehmens unter
              <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy?tid=111779921" target="_blank" rel="noopener noreferrer">
                https://wiki.osmfoundation.org/wiki/Privacy_Policy.
              </a>
            </p>
            <h2 id="google-fonts-datenschutzerklaerung">Google Fonts Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>Google Fonts Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Optimierung unserer Serviceleistung</li>
              <li>Verarbeitete Daten: Daten wie etwa IP-Adresse und CSS- und Schrift-Anfragen</li>
              <li>Mehr Details dazu finden Sie weiter unten in dieser Datenschutzerklärung.</li>
              <li>Speicherdauer: Font-Dateien werden bei Google ein Jahr gespeichert</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was sind Google Fonts?</h3>
            <p>
              Auf unserer Website verwenden wir Google Fonts. Das sind die “Google-Schriften” der Firma Google Inc. Für den europäischen Raum ist das
              Unternehmen Google Ireland Limited (Gordon House, Barrow Street Dublin 4, Irland) für alle Google-Dienste verantwortlich.
            </p>
            <p>
              Für die Verwendung von Google-Schriftarten müssen Sie sich nicht anmelden bzw. ein Passwort hinterlegen. Weiters werden auch keine
              Cookies in Ihrem Browser gespeichert. Die Dateien (CSS, Schriftarten/Fonts) werden über die Google-Domains fonts.googleapis.com und
              fonts.gstatic.com angefordert. Laut Google sind die Anfragen nach CSS und Schriften vollkommen getrennt von allen anderen
              Google-Diensten. Wenn Sie ein Google-Konto haben, brauchen Sie keine Sorge haben, dass Ihre Google-Kontodaten, während der Verwendung
              von Google Fonts, an Google übermittelt werden. Google erfasst die Nutzung von CSS (Cascading Style Sheets) und der verwendeten
              Schriftarten und speichert diese Daten sicher. Wie die Datenspeicherung genau aussieht, werden wir uns noch im Detail ansehen.
            </p>
            <p>
              Google Fonts (früher Google Web Fonts) ist ein Verzeichnis mit über 800 Schriftarten, die
              <a href="https://de.wikipedia.org/wiki/Google_LLC?tid=111779921">Google</a>&nbsp;Ihren Nutzern kostenlos zu Verfügung stellen.
            </p>
            <p>
              Viele dieser Schriftarten sind unter der&nbsp;SIL Open Font License&nbsp;veröffentlicht, während andere unter
              der&nbsp;Apache-Lizenz&nbsp;veröffentlicht wurden. Beides sind&nbsp;freie Software-Lizenzen.
            </p>
            <h3>Warum verwenden wir Google Fonts auf unserer Website?</h3>
            <p>
              Mit Google Fonts können wir auf der eigenen Webseite Schriften nutzen, und müssen sie nicht auf unserem eigenen Server hochladen. Google
              Fonts ist ein wichtiger Baustein, um die Qualität unserer Webseite hoch zu halten. Alle Google-Schriften sind automatisch für das Web
              optimiert und dies spart Datenvolumen und ist speziell für die Verwendung bei mobilen Endgeräten ein großer Vorteil. Wenn Sie unsere
              Seite besuchen, sorgt die niedrige Dateigröße für eine schnelle Ladezeit. Des Weiteren sind Google Fonts sichere Web Fonts.
              Unterschiedliche Bildsynthese-Systeme (Rendering) in verschiedenen Browsern, Betriebssystemen und mobilen Endgeräten können zu Fehlern
              führen. Solche Fehler können teilweise Texte bzw. ganze Webseiten optisch verzerren. Dank des schnellen Content Delivery Network (CDN)
              gibt es mit Google Fonts keine plattformübergreifenden Probleme. Google Fonts unterstützt alle gängigen Browser (Google
              Chrome,&nbsp;Mozilla Firefox,&nbsp;Apple Safari,&nbsp;Opera) und funktioniert zuverlässig auf den meisten modernen mobilen
              Betriebssystemen, einschließlich Android 2.2+ und iOS 4.2+ (iPhone, iPad, iPod). Wir verwenden die Google Fonts also, damit wir unser
              gesamtes Online-Service so schön und einheitlich wie möglich darstellen können.
            </p>
            <h3>Welche Daten werden von Google gespeichert?</h3>
            <p>
              Wenn Sie unsere Webseite besuchen, werden die Schriften über einen Google-Server nachgeladen. Durch diesen externen Aufruf werden Daten
              an die Google-Server übermittelt. So erkennt Google auch, dass Sie bzw. Ihre IP-Adresse unsere Webseite besucht. Die Google Fonts API
              wurde entwickelt, um Verwendung, Speicherung und Erfassung von Endnutzerdaten auf das zu reduzieren, was für eine ordentliche
              Bereitstellung von Schriften nötig ist.&nbsp;API steht übrigens für „Application Programming Interface“ und dient unter anderem als
              Datenübermittler im Softwarebereich.
            </p>
            <p>
              Google Fonts speichert CSS- und Schrift-Anfragen sicher bei Google und ist somit geschützt.&nbsp;Durch die gesammelten Nutzungszahlen
              kann Google feststellen, wie gut die einzelnen Schriften ankommen. Die Ergebnisse veröffentlicht Google auf internen Analyseseiten, wie
              beispielsweise Google Analytics. Zudem verwendet Google auch Daten des eigenen Web-Crawlers, um festzustellen, welche Webseiten
              Google-Schriften verwenden. Diese Daten werden in der BigQuery-Datenbank von Google Fonts veröffentlicht. Unternehmer und Entwickler
              nützen das Google-Webservice&nbsp;BigQuery, um große Datenmengen untersuchen und bewegen zu können.
            </p>
            <p>
              Zu bedenken gilt allerdings noch, dass durch jede Google Font Anfrage auch Informationen wie&nbsp;Spracheinstellungen, IP-Adresse,
              Version des Browsers, Bildschirmauflösung des Browsers und Name des Browsers automatisch an die Google-Server übertragen werden. Ob
              diese Daten auch gespeichert werden, ist nicht klar feststellbar bzw. wird von Google nicht eindeutig kommuniziert.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Anfragen für CSS-Assets speichert Google einen Tag lang auf seinen Servern, die hauptsächlich außerhalb der EU angesiedelt sind. Das
              ermöglicht uns, mithilfe eines Google-Stylesheets die Schriftarten zu nutzen. Ein Stylesheet ist eine Formatvorlage, über die man
              einfach und schnell z.B. das Design bzw. die Schriftart einer Webseite ändern kann.
            </p>
            <p>
              Die Font-Dateien werden bei Google ein Jahr gespeichert. Google verfolgt damit das Ziel, die Ladezeit von Webseiten grundsätzlich zu
              verbessern. Wenn Millionen von Webseiten auf die gleichen Schriften verweisen, werden sie nach dem ersten Besuch zwischengespeichert und
              erscheinen sofort auf allen anderen später besuchten Webseiten wieder. Manchmal aktualisiert Google Schrifulateien, um die Dateigröße zu
              reduzieren, die Abdeckung von Sprache zu erhöhen und das Design zu verbessern.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Jene Daten, die Google für einen Tag bzw. ein Jahr speichert können nicht einfach gelöscht werden. Die Daten werden beim Seitenaufruf
              automatisch an Google übermittelt. Um diese Daten vorzeitig löschen zu können, müssen Sie den Google-Support auf
              <a href="https://support.google.com/?hl=de&tid=111779921">https://support.google.com/?hl=de&amp;tid=111779921</a> kontaktieren.
              Datenspeicherung verhindern Sie in diesem Fall nur, wenn Sie unsere Seite nicht besuchen.
            </p>
            <p>
              Anders als andere Web-Schriften erlaubt uns Google uneingeschränkten Zugriff auf alle Schriftarten. Wir können also unlimitiert auf ein
              Meer an Schriftarten zugreifen und so das Optimum für unsere Webseite rausholen. Mehr zu Google Fonts und weiteren Fragen finden Sie auf
              <a href="https://developers.google.com/fonts/faq?tid=111779921">https://developers.google.com/fonts/faq?tid=111779921</a>. Dort geht
              zwar Google auf datenschutzrelevante Angelegenheiten ein, doch wirklich detaillierte Informationen über Datenspeicherung sind nicht
              enthalten. Es ist relativ schwierig, von Google wirklich präzise Informationen über gespeicherten Daten zu bekommen.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Wenn Sie eingewilligt haben, dass Google Fonts eingesetzt werden darf, ist die Rechtsgrundlage der entsprechenden Datenverarbeitung
              diese Einwilligung. Diese Einwilligung stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für
              die Verarbeitung personenbezogener Daten, wie sie bei der Erfassung durch Google Fonts vorkommen kann, dar.
            </p>
            <p>
              Von unserer Seite besteht zudem ein berechtigtes Interesse, Google Font zu verwenden, um unser Online-Service zu optimieren. Die dafür
              entsprechende Rechtsgrundlage ist <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>. Wir setzen Google Font
              gleichwohl nur ein, soweit Sie eine Einwilligung erteilt haben.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch Google Fonts. Dies kann dazu führen, dass gegebenenfalls Daten
              nicht anonymisiert verarbeitet und gespeichert werden. Ferner können gegebenenfalls US-amerikanische staatliche Behörden Zugriff auf
              einzelne Daten nehmen. Es kann ferner vorkommen, dass diese Daten mit Daten aus möglichen anderen Diensten von Google, bei denen Sie ein
              Nutzerkonto haben, verknüpft werden.
            </p>
            <p>
              Welche Daten grundsätzlich von Google erfasst werden und wofür diese Daten verwendet werden, können Sie auch auf
              <a href="https://policies.google.com/privacy?hl=de&tid=111779921">https://www.google.com/intl/de/policies/privacy/</a> nachlesen.
            </p>
            <h2 id="font-awesome-datenschutzerklaerung">Font Awesome Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>Font Awesome Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Optimierung unserer Serviceleistung</li>
              <li>Verarbeitete Daten: etwa IP-Adresse und und welche Icon-Dateien geladen werden</li>
              <li>Mehr Details dazu finden Sie weiter unten in dieser Datenschutzerklärung.</li>
              <li>Speicherdauer: Dateien in identifizierbarer Form werden wenige Wochen gespeichert</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist Font Awesome?</h3>
            <p>
              Wir verwenden auf unserer Website Font Awesome des amerikanischen Unternehmens Fonticons (307 S. Main St., Suite 202, Bentonville, AR
              72712, USA). Wenn Sie eine unserer Webseite aufrufen, wird die Web-Schriftart Font Awesome (im Speziellen Icons) über das Font Awesome
              Content Delivery Netzwerk (CDN) geladen. So werden die Texte bzw. Schriften und Icons auf jedem Endgerät passend angezeigt. In dieser
              Datenschutzerklärung gehen wir näher auf die Datenspeicherung und Datenverarbeitung durch diesen Service ein.
            </p>
            <p>
              Icons spielen für Websites eine immer wichtigere Rolle. Font Awesome ist eine Web-Schriftart, die speziell für Webdesigner und
              Webentwickler entwickelt wurde. Mit Font Awesome können etwa Icons mit Hilfe der Stylesheet-Sprache CSS nach Belieben skaliert und
              gefärbt werden. Sie ersetzen so alte Bild-Icons. Font Awesome CDN ist der einfachste Weg die Icons oder Schriftarten auf Ihre Website zu
              laden. Dafür mussten wir nur eine kleine Code-Zeile in unsere Website einbinden.
            </p>
            <h3>Warum verwenden wir Font Awesome auf unserer Website?</h3>
            <p>
              Durch Font Awesome können Inhalte auf unserer Website besser aufbereitet werden. So können Sie sich auf unserer Website besser
              orientieren und die Inhalte leichter erfassen. Mit den Icons kann man sogar manchmal ganze Wörter ersetzen und Platz sparen. Da ist
              besonders praktisch, wenn wir Inhalte speziell für Smartphones optimieren.&nbsp; Diese Icons werden statt als Bild als HMTL-Code
              eingefügt. Dadurch können wir die Icons mit CSS genauso bearbeiten, wie wir wollen. Gleichzeitig verbessern wir mit Font Awesome auch
              unsere Ladegeschwindigkeit, weil es sich nur um HTML-Elemente handelt und nicht um Icon-Bilder. All diese Vorteile helfen uns, die
              Website für Sie noch übersichtlicher, frischer und schneller zu machen.
            </p>
            <h3>Welche Daten werden von Font Awesome gespeichert?</h3>
            <p>
              Zum Laden von Icons und Symbolen wird das Font Awesome Content Delivery Network (CDN) verwendet. CDNs sind Netzwerke von Servern, die
              weltweit verteilt sind und es möglich machen, schnell Dateien aus der Nähe zu laden. So werden auch, sobald Sie eine unserer Seiten
              aufrufen, die entsprechenden Icons von Font Awesome bereitgestellt.
            </p>
            <p>
              Damit die Web-Schriftarten geladen werden können, muss Ihr Browser eine Verbindung zu den Servern des Unternehmens Fonticons, Inc.
              herstellen. Dabei wird Ihre IP-Adresse erkannt. Font Awesome sammelt auch Daten darüber, welche Icon-Dateien wann heruntergeladen
              werden. Weiters werden auch technische Daten wie etwa Ihre Browser-Version, Bildschirmauflösung oder der Zeitpunkt der ausgerufenen
              Seite übertragen.
            </p>
            <p>Aus folgenden Gründen werden diese Daten gesammelt und gespeichert:</p>
            <ul>
              <li>um Content Delivery Netzwerke zu optimieren</li>
              <li>um technische Fehler zu erkennen und zu beheben</li>
              <li>um CDNs vor Missbrauch und Angriffen zu schützen</li>
              <li>um Gebühren von Font Awesome Pro-Kunden berechnen zu können</li>
              <li>um die Beliebtheit von Icons zu erfahren</li>
              <li>um zu wissen, welchen Computer und welche Software Sie verwenden</li>
            </ul>
            <p>
              Falls Ihr Browser Web-Schriftarten nicht zulässt, wird automatisch eine Standardschrift Ihres PCs verwendet. Nach derzeitigem Stand
              unserer Erkenntnis werden keine Cookies gesetzt. Wir sind mit der Datenschutzabteilung von Font Awesome in Kontakt und geben Ihnen
              Bescheid, sobald wir näheres in Erfahrung bringen.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Font Awesome speichert Daten über die Nutzung des Content Delivery Network auf Servern auch in den Vereinigten Staaten von Amerika. Die
              CDN-Server befinden sich allerdings weltweit und speichern Userdaten, wo Sie sich befinden. In identifizierbarer Form werden die Daten
              in der Regel nur wenige Wochen gespeichert. Aggregierte Statistiken über die Nutzung von den CDNs können auch länger gespeichert werden.
              Personenbezogene Daten sind hier nicht enthalten.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Font Awesome speichert nach aktuellem Stand unseres Wissens keine personenbezogenen Daten über die Content Delivery Netzwerke. Wenn Sie
              nicht wollen, dass Daten über die verwendeten Icons gespeichert werden, können Sie leider unsere Website nicht besuchen. Wenn Ihr
              Browser keine Web-Schriftarten erlaubt, werden auch keine Daten übertragen oder gespeichert. In diesem Fall wird einfach die
              Standard-Schrift Ihres Computers verwendet.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Wenn Sie eingewilligt haben, dass Font Awesome eingesetzt werden darf, ist die Rechtsgrundlage der entsprechenden Datenverarbeitung
              diese Einwilligung. Diese Einwilligung stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für
              die Verarbeitung personenbezogener Daten, wie sie bei der Erfassung durch Font Awesome vorkommen kann, dar.
            </p>
            <p>
              Von unserer Seite besteht zudem ein berechtigtes Interesse, Font Awesome zu verwenden, um unser Online-Service zu optimieren. Die dafür
              entsprechende Rechtsgrundlage ist <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>. Wir setzen Font Awesome
              gleichwohl nur ein, soweit Sie eine Einwilligung erteilt haben.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch Font Awesome. Dies kann dazu führen, dass gegebenenfalls Daten
              nicht anonymisiert verarbeitet und gespeichert werden. Ferner können gegebenenfalls US-amerikanische staatliche Behörden Zugriff auf
              einzelne Daten nehmen. Es kann ferner vorkommen, dass diese Daten mit Daten aus möglichen anderen Diensten von Font Awesome, bei denen
              Sie ein Nutzerkonto haben, verknüpft werden.
            </p>
            <p>
              Wenn Sie mehr über Font Awesome und deren Umgang mit Daten erfahren wollen, empfehlen wir Ihnen die Datenschutzerklärung unter
              <a href="https://fontawesome.com/privacy">https://fontawesome.com/privacy</a> und die Hilfeseite unter
              <a href="https://fontawesome.com/help?tid=111779921">https://fontawesome.com/help</a>.
            </p>
            <h2 id="google-recaptcha-datenschutzerklaerung">Google reCAPTCHA Datenschutzerklärung</h2>
            <ul>
              <li>
                <strong>Google reCAPTCHA Datenschutzerklärung Zusammenfassung</strong>
              </li>
              <li>Betroffene: Besucher der Website</li>
              <li>Zweck: Optimierung unserer Serviceleistung und Schutz vor Cyberangriffen</li>
              <li>
                Verarbeitete Daten: Daten wie etwa IP-Adresse, Browserinformationen, Ihr Betriebssystem, eingeschränkte Standorts- und Nutzungsdaten
              </li>
              <li>Mehr Details dazu finden Sie weiter unten in dieser Datenschutzerklärung.</li>
              <li>Speicherdauer: abhängig von den gespeicherten Daten</li>
              <li>Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung), Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</li>
            </ul>
            <h3>Was ist reCAPTCHA?</h3>
            <p>
              Unser oberstes Ziel ist es, unsere Webseite für Sie und für uns bestmöglich zu sichern und zu schützen. Um das zu gewährleisten,
              verwenden wir Google reCAPTCHA der Firma Google Inc. Für den europäischen Raum ist das Unternehmen Google Ireland Limited (Gordon House,
              Barrow Street Dublin 4, Irland) für alle Google-Dienste verantwortlich. Mit reCAPTCHA können wir feststellen, ob Sie auch wirklich ein
              Mensch aus Fleisch und Blut sind und kein Roboter oder eine andere Spam-Software. Unter Spam verstehen wir jede, auf elektronischen Weg,
              unerwünschte Information, die uns ungefragter Weise zukommt. Bei den klassischen CAPTCHAS mussten Sie zur Überprüfung meist Text- oder
              Bildrätsel lösen. Mit reCAPTCHA von Google müssen wir Sie meist nicht mit solchen Rätseln belästigen. Hier reicht es in den meisten
              Fällen, wenn Sie einfach ein Häkchen setzen und so bestätigen, dass Sie kein Bot sind. Mit der neuen Invisible reCAPTCHA Version müssen
              Sie nicht mal mehr ein Häkchen setzen. Wie das genau funktioniert und vor allem welche Daten dafür verwendet werden, erfahren Sie im
              Verlauf dieser Datenschutzerklärung.
            </p>
            <p>
              reCAPTCHA ist ein freier Captcha-Dienst von Google, der Webseiten vor Spam-Software und den Missbrauch durch nicht-menschliche Besucher
              schützt. Am häufigsten wird dieser Dienst verwendet, wenn Sie Formulare im Internet ausfüllen. Ein Captcha-Dienst ist eine Art
              automatischer Turing-Test, der sicherstellen soll, dass eine Handlung im Internet von einem Menschen und nicht von einem Bot vorgenommen
              wird. Im klassischen Turing-Test (benannt nach dem Informatiker Alan Turing) stellt ein Mensch die Unterscheidung zwischen Bot und
              Mensch fest. Bei Captchas übernimmt das auch der Computer bzw. ein Softwareprogramm. Klassische Captchas arbeiten mit kleinen Aufgaben,
              die für Menschen leicht zu lösen sind, doch für Maschinen erhebliche Schwierigkeiten aufweisen. Bei reCAPTCHA müssen Sie aktiv keine
              Rätsel mehr lösen. Das Tool verwendet moderne Risikotechniken, um Menschen von Bots zu unterscheiden. Hier müssen Sie nur noch das
              Textfeld „Ich bin kein Roboter“ ankreuzen bzw. bei Invisible reCAPTCHA ist selbst das nicht mehr nötig. Bei reCAPTCHA wird ein
              JavaScript-Element in den Quelltext eingebunden und dann läuft das Tool im Hintergrund und analysiert Ihr Benutzerverhalten. Aus diesen
              Useraktionen berechnet die Software einen sogenannten Captcha-Score. Google berechnet mit diesem Score schon vor der Captcha-Eingabe wie
              hoch die Wahrscheinlichkeit ist, dass Sie ein Mensch sind. reCAPTCHA bzw. Captchas im Allgemeinen kommen immer dann zum Einsatz, wenn
              Bots gewisse Aktionen (wie z.B. Registrierungen, Umfragen usw.) manipulieren oder missbrauchen könnten.
            </p>
            <h3>Warum verwenden wir reCAPTCHA auf unserer Website?</h3>
            <p>
              Wir wollen nur Menschen aus Fleisch und Blut auf unserer Seite begrüßen. Bots oder Spam-Software unterschiedlichster Art dürfen getrost
              zuhause bleiben. Darum setzen wir alle Hebel in Bewegung, uns zu schützen und die bestmögliche Benutzerfreundlichkeit für Sie
              anzubieten. Aus diesem Grund verwenden wir Google reCAPTCHA der Firma Google. So können wir uns ziemlich sicher sein, dass wir eine
              „botfreie“ Webseite bleiben. Durch die Verwendung von reCAPTCHA werden Daten an Google übermittelt, um festzustellen, ob Sie auch
              wirklich ein Mensch sind. reCAPTCHA dient also der Sicherheit unserer Webseite und in weiterer Folge damit auch Ihrer Sicherheit. Zum
              Beispiel könnte es ohne reCAPTCHA passieren, dass bei einer Registrierung ein Bot möglichst viele E-Mail-Adressen registriert, um im
              Anschluss Foren oder Blogs mit unerwünschten Werbeinhalten „zuzuspamen“. Mit reCAPTCHA können wir solche Botangriffe vermeiden.
            </p>
            <h3>Welche Daten werden von reCAPTCHA gespeichert?</h3>
            <p>
              reCAPTCHA sammelt personenbezogene Daten von Usern, um festzustellen, ob die Handlungen auf unserer Webseite auch wirklich von Menschen
              stammen. Es kann also die IP-Adresse und andere Daten, die Google für den reCAPTCHA-Dienst benötigt, an Google versendet werden.
              IP-Adressen werden innerhalb der Mitgliedstaaten der EU oder anderer Vertragsstaaten des Abkommens über den Europäischen Wirtschaftsraum
              fast immer zuvor gekürzt, bevor die Daten auf einem Server in den USA landen. Die IP-Adresse wird nicht mit anderen Daten von Google
              kombiniert, sofern Sie nicht während der Verwendung von reCAPTCHA mit Ihrem Google-Konto angemeldet sind. Zuerst prüft der
              reCAPTCHA-Algorithmus, ob auf Ihrem Browser schon Google-Cookies von anderen Google-Diensten (YouTube. Gmail usw.) platziert sind.
              Anschließend setzt reCAPTCHA ein zusätzliches Cookie in Ihrem Browser und erfasst einen Schnappschuss Ihres Browserfensters.
            </p>
            <p>
              Die folgende Liste von gesammelten Browser- und Userdaten, hat nicht den Anspruch auf Vollständigkeit. Vielmehr sind es Beispiele von
              Daten, die nach unserer Erkenntnis, von Google verarbeitet werden.
            </p>
            <ul>
              <li>Referrer URL (die Adresse der Seite von der der Besucher kommt)</li>
              <li>IP-Adresse (z.B. 256.123.123.1)</li>
              <li>
                Infos über das Betriebssystem&nbsp;(die Software, die den Betrieb Ihres Computers ermöglicht. Bekannte Betriebssysteme sind Windows,
                Mac OS X oder Linux)
              </li>
              <li>Cookies&nbsp;(kleine Texulateien, die Daten in Ihrem Browser speichern)</li>
              <li>Maus- und Keyboardverhalten&nbsp;(jede Aktion, die Sie mit der Maus oder der Tastatur ausführen wird gespeichert)</li>
              <li>Datum und Spracheinstellungen&nbsp;(welche Sprache bzw. welches Datum Sie auf Ihrem PC voreingestellt haben wird gespeichert)</li>
              <li>
                Alle Javascript-Objekte&nbsp;(JavaScript ist eine Programmiersprache, die Webseiten ermöglicht, sich an den User anzupassen.
                JavaScript-Objekte können alle möglichen Daten unter einem Namen sammeln)
              </li>
              <li>Bildschirmauflösung&nbsp;(zeigt an aus wie vielen Pixeln die Bilddarstellung besteht)</li>
            </ul>
            <p>
              Unumstritten ist, dass Google diese Daten verwendet und analysiert noch bevor Sie auf das Häkchen „Ich bin kein Roboter“ klicken. Bei
              der Invisible reCAPTCHA-Version fällt sogar das Ankreuzen weg und der ganze Erkennungsprozess läuft im Hintergrund ab. Wie viel und
              welche Daten Google genau speichert, erfährt man von Google nicht im Detail.
            </p>
            <h3>Wie lange und wo werden die Daten gespeichert?</h3>
            <p>
              Durch das Einfügen von reCAPTCHA werden Daten von Ihnen auf den Google-Server übertragen. Wo genau diese Daten gespeichert werden,
              stellt Google, selbst nach wiederholtem Nachfragen, nicht klar dar. Ohne eine Bestätigung von Google erhalten zu haben, ist davon
              auszugehen, dass Daten wie Mausinteraktion, Verweildauer auf der Webseite oder Spracheinstellungen auf den europäischen oder
              amerikanischen Google-Servern gespeichert werden. Die IP-Adresse, die Ihr Browser an Google übermittelt, wird grundsätzlich nicht mit
              anderen Google-Daten aus weiteren Google-Diensten zusammengeführt. Wenn Sie allerdings während der Nutzung des reCAPTCHA-Plug-ins bei
              Ihrem Google-Konto angemeldet sind, werden die Daten zusammengeführt.<strong>&nbsp;</strong>Dafür gelten die abweichenden
              Datenschutzbestimmungen der Firma Google.
            </p>
            <h3>Wie kann ich meine Daten löschen bzw. die Datenspeicherung verhindern?</h3>
            <p>
              Wenn Sie wollen, dass über Sie und über Ihr Verhalten keine Daten an Google übermittelt werden, müssen Sie sich, bevor Sie unsere
              Webseite besuchen bzw. die reCAPTCHA-Software verwenden, bei Google vollkommen ausloggen und alle Google-Cookies
              löschen.&nbsp;Grundsätzlich werden die Daten sobald Sie unsere Seite aufrufen automatisch an Google übermittelt. Um diese Daten wieder
              zu löschen, müssen Sie den Google-Support auf&nbsp;
              <a href="https://support.google.com/?hl=de&tid=111779921" target="_blank" rel="noopener noreferrer">
                https://support.google.com/?hl=de&amp;tid=111779921
              </a>
              kontaktieren.
            </p>
            <p>
              Wenn Sie also unsere Webseite verwenden, erklären Sie sich einverstanden, dass Google LLC und deren Vertreter automatisch Daten erheben,
              bearbeiten und nutzen.
            </p>
            <p>
              Bitte beachten Sie, dass bei der Verwendung dieses Tools Daten von Ihnen auch außerhalb der EU gespeichert und verarbeitet werden
              können. Die meisten Drittstaaten (darunter auch die USA) gelten nach derzeitigem europäischen Datenschutzrecht als nicht sicher. Daten
              an unsichere Drittstaaten dürfen also nicht einfach übertragen, dort gespeichert und verarbeitet werden, sofern es keine passenden
              Garantien (wie etwa EU-Standardvertragsklauseln) zwischen uns und dem außereuropäischen Dienstleister gibt.
            </p>
            <h3>Rechtsgrundlage</h3>
            <p>
              Wenn Sie eingewilligt haben, dass Google reCAPTCHA eingesetzt werden darf, ist die Rechtsgrundlage der entsprechenden Datenverarbeitung
              diese Einwilligung. Diese Einwilligung stellt laut<strong> Art. 6 Abs. 1 lit. a DSGVO (Einwilligung)</strong> die Rechtsgrundlage für
              die Verarbeitung personenbezogener Daten, wie sie bei der Erfassung durch Google reCAPTCHA vorkommen kann, dar.
            </p>
            <p>
              Von unserer Seite besteht zudem ein berechtigtes Interesse, Google reCAPTCHA zu verwenden, um unser Online-Service zu optimieren und
              sicherer zu machen. Die dafür entsprechende Rechtsgrundlage ist <strong>Art. 6 Abs. 1 lit. f DSGVO (Berechtigte Interessen)</strong>.
              Wir setzen Google reCAPTCHA gleichwohl nur ein, soweit Sie eine Einwilligung erteilt haben.
            </p>
            <p>
              Wir weisen darauf hin, dass nach Meinung des Europäischen Gerichtshofs derzeit kein angemessenes Schutzniveau für den Datentransfer in
              die USA besteht. Die Datenverarbeitung geschieht im Wesentlichen durch Google reCAPTCHA. Dies kann dazu führen, dass gegebenenfalls
              Daten nicht anonymisiert verarbeitet und gespeichert werden. Ferner können gegebenenfalls US-amerikanische staatliche Behörden Zugriff
              auf einzelne Daten nehmen. Es kann ferner vorkommen, dass diese Daten mit Daten aus möglichen anderen Diensten von Google, bei denen Sie
              ein Nutzerkonto haben, verknüpft werden.
            </p>
            <p>
              Etwas mehr über reCAPTCHA erfahren Sie auf der Webentwickler-Seite von Google auf
              <a href="https://developers.google.com/recaptcha/" target="_blank" rel="noopener noreferrer">
                https://developers.google.com/recaptcha/
              </a>
              . Google geht hier zwar auf die technische Entwicklung der reCAPTCHA näher ein, doch genaue Informationen über Datenspeicherung und
              datenschutzrelevanten Themen sucht man auch dort vergeblich. Eine gute Übersicht über die grundsätzliche Verwendung von Daten bei Google
              finden Sie in der hauseigenen Datenschutzerklärung auf
              <a href="https://policies.google.com/privacy?hl=de&tid=111779921" target="_blank" rel="noopener noreferrer">
                https://www.google.com/intl/de/policies/privacy/
              </a>
              .
            </p>
            Alle Texte sind urheberrechtlich geschützt.
            <p className="text-xs">
              Quelle: Erstellt mit dem
              <a href="https://www.adsimple.at/datenschutz-generator/" target="_blank" rel="noreferrer">
                Datenschutz Generator
              </a>
              von AdSimple
            </p>
          </div>
        </section>
        <section className="bg-blue-100 py-10 dsgvo" id="english-version">
          <div className="container max-w-screen-lg mx-auto text-center px-5">
            <hr className="border-uclight my-8 w-3/4 mx-auto" />
            <h1>Privacy Policy</h1>
            <h2 id="einleitung-ueberblick-en">Privacy Policy</h2>
            <p>
              We have written this privacy policy (version 13.07.2021-111779921) in order to explain to you, in accordance with the provisions of the
              <a
                href="https://eur-lex.europa.eu/legal-content/EN/ALL/?uri=celex%3A32016R0679&amp%3Btid=%5Badsimpletextid]"
                target="_blank"
                rel="noreferrer"
              >
                General Data Protection Regulation (EU) 2016/679
              </a>
              and applicable national laws, which personal data (data for short) we as the controller – and the processors commissioned by us (e.g.
              providers) – process, will process in the future and what legal options you have. The terms used are to be considered as gender-neutral.
              <br />
              <strong>In short:</strong> We provide you with comprehensive information about any personal data we process about you.
            </p>
            <p>
              Privacy policies usually sound very technical and use legal terminology. However, this privacy policy is intended to describe the most
              important things to you as simply and transparently as possible. So long as it aids transparency, technical
              <strong>terms are explained in a reader-friendly manner, links</strong> to further information are provided and{" "}
              <strong>graphics</strong>
              are used. We are thus informing in clear and simple language that we only process personal data in the context of our business
              activities if there is a legal basis for it. This is certainly not possible with brief, unclear and legal-technical statements, as is
              often standard on the Internet when it comes to data protection. I hope you find the following explanations interesting and informative.
              Maybe you will also find some information that you have not been familiar with.
              <br />
              If you still have questions, we would like to ask you to contact the responsible body named below or in the imprint, to follow the
              existing links and to look at further information on third-party sites. You can of course also find our contact details in the imprint.
            </p>
            <h2 id="anwendungsbereich-en">Scope</h2>
            <p>
              This privacy policy applies to all personal data processed by our company and to all personal data processed by companies commissioned
              by us (processors). With the term personal data, we refer to information within the meaning of Article 4 No. 1 GDPR, such as the name,
              email address and postal address of a person. The processing of personal data ensures that we can offer and invoice our services and
              products, be it online or offline. The scope of this privacy policy includes:
            </p>
            <ul>
              <li>all online presences (websites, online shops) that we operate</li>
              <li>Social media presences and email communication</li>
              <li>mobile apps for smartphones and other devices</li>
            </ul>
            <p>
              <strong>In short:</strong> This privacy policy applies to all areas in which personal data is processed in a structured manner by the
              company via the channels mentioned. Should we enter into legal relations with you outside of these channels, we will inform you
              separately if necessary.
            </p>
            <h2 id="rechtsgrundlagen-en">Legal bases</h2>
            <p>
              In the following privacy policy, we provide you with transparent information on the legal principles and regulations, i.e. the legal
              bases of the General Data Protection Regulation, which enable us to process personal data.
              <br />
              Whenever EU law is concerned, we refer to REGULATION (EU) 2016/679 OF THE EUROPEAN PARLIAMENT AND OF THE COUNCIL of April 27, 2016. You
              can of course access the General Data Protection Regulation of the EU online at EUR-Lex, the gateway to EU law, at
              <a href="https://eur-lex.europa.eu/legal-content/DE/ALL/?uri=celex%3A32016R0679&tid=111779921" target="_blank" rel="noreferrer">
                https://eur-lex.europa.eu/legal-content/EN/TXT/?uri=celex%3A32016R0679
              </a>
              .
            </p>
            <p>We only process your data if at least one of the following conditions applies:</p>
            <ol>
              <li>
                <strong>Consent</strong> (Article 6 Paragraph 1 lit. a GDPR): You have given us your consent to process data for a specific purpose.
                An example would be the storage of data you entered into a contact form.
              </li>
              <li>
                <strong>Contract</strong> (Article 6 Paragraph 1 lit. b GDPR): We process your data in order to fulfill a contract or pre-contractual
                obligations with you. For example, if we conclude a sales contract with you, we need personal information in advance.
              </li>
              <li>
                <strong>Legal obligation</strong> (Article 6 Paragraph 1 lit. c GDPR): If we are subject to a legal obligation, we will process your
                data. For example, we are legally required to keep invoices for our bookkeeping. These usually contain personal data.
              </li>
              <li>
                <strong>Legitimate interests</strong> (Article 6 Paragraph 1 lit. f GDPR): In the case of legitimate interests that do not restrict
                your basic rights, we reserve the right to process personal data. For example, we have to process certain data in order to be able to
                operate our website securely and economically. Therefore, the processing is a legitimate interest.
              </li>
            </ol>
            <p>
              Other conditions such as making recordings in the interest of the public, the exercise of official authority as well as the protection
              of vital interests do not usually occur with us. Should such a legal basis be relevant, it will be disclosed in the appropriate place.
            </p>
            <p>In addition to the EU regulation, national laws also apply:</p>
            <ul>
              <li>
                In <strong>Austria</strong> this is the Austrian Data Protection Act (<strong>Datenschutzgesetz</strong>), in short
                <strong>DSG</strong>.
              </li>
              <li>
                In <strong>Germany</strong> this is the Federal Data Protection Act (<strong>Bundesdatenschutzgesetz</strong>), in short
                <strong>BDSG</strong>.
              </li>
            </ul>
            <p>Should other regional or national laws apply, we will inform you about them in the following sections.</p>
            <h2 id="kontaktdaten-verantwortliche-en">Contact details of the data protection controller</h2>
            <p>
              If you have any questions about data protection, you will find the contact details of the responsible person or controller below:
              <br />
              Imprint:
              <Link to="/imprint" target="_blank" rel="noreferrer">
                https://www.ubicube.eu/imprint
              </Link>
            </p>
            <h2 id="speicherdauer-en">Storage Period</h2>
            <p>
              It is a general criterion for us to store personal data only for as long as is absolutely necessary for the provision of our services
              and products. This means that we delete personal data as soon as any reason for the data processing no longer exists. In some cases, we
              are legally obliged to keep certain data stored even after the original purpose no longer exists, such as for accounting purposes.
            </p>
            <p>
              If you want your data to be deleted or if you want to revoke your consent to data processing, the data will be deleted as soon as
              possible, provided there is no obligation to continue its storage.
            </p>
            <p>We will inform you below about the specific duration of the respective data processing, provided we have further information.</p>
            <h2 id="rechte-dsgvo-en">Rights in accordance with the General Data Protection Regulation</h2>
            <p>According to Article 13 of the GDPR, you have the following rights to ensure your data is processed fairly and transparently:</p>
            <ul>
              <li>
                According to Article 15 of the GDPR, you have a right to information as to whether we are processing your data. If this is the case,
                you have the right to receive a copy of the data in question, as well as the following information:
                <ul>
                  <li>the purpose of the processing;</li>
                  <li>the categories of personal data concerned;</li>
                  <li>
                    the recipients to whom the personal data is disclosed, and in particular how security can be guaranteed if the data is transferred
                    to third countries;
                  </li>
                  <li>how long the data will be stored;</li>
                  <li>
                    the existence of the right to request rectification, erasure or restriction of processing of personal data and the right to object
                    to such processing;
                  </li>
                  <li>the right to lodge a complaint with a supervisory authority (you can find links to these authorities below);</li>
                  <li>the source of the data, if we have not collected it from you directly;</li>
                  <li>
                    whether profiling is carried out, i.e. whether data is automatically evaluated in order to be allocated to a personal profile on
                    you.
                  </li>
                </ul>
              </li>
              <li>
                According to Article 16 of the GDPR, you have the right to request rectification of the data, which means that we have to correct your
                data should you find any errors.
              </li>
              <li>
                According to Article 17 of the GDPR, you have the right to have your personal data erased (the “right to be forgotten”), which
                specifically means that you can request to have your data deleted.
              </li>
              <li>
                According to Article 18 of the GDPR, you have the right to obtain restriction of processing, which means that we are only allowed to
                store the data but no longer use or process it.
              </li>
              <li>
                According to Article 20 of the GDPR, you have the right to request data portability, which means that on request, we will provide you
                with your data in a common format.
              </li>
              <li>
                According to Article 21 of the GDPR, you have a right to object, which will result in a change in processing after implementation.
                <ul>
                  <li>
                    If the processing of your data is based on Article 6 (1) (e) (public interest, exercise of official authority) or Article 6 (1)
                    (f) (legitimate interest), you can object to the processing. We will then check as soon as possible, whether we can legally comply
                    with this objection.
                  </li>
                  <li>
                    If data is used to operate direct mail, you can object to this type of data processing at any time. We will then no longer be
                    allowed to use your data for direct marketing purposes.
                  </li>
                  <li>
                    If your data is used for profiling, you can object to this type of data processing at any time. We will then no longer be allowed
                    to use your data for profiling purposes.
                  </li>
                </ul>
              </li>
              <li>
                According to Article 22 of the GDPR, you may have the right not to be subject to a decision based solely on automated processing
                (including profiling).
              </li>
            </ul>
            <p>
              If you believe that the processing of your data violates the data protection law, or your data protection claims have been violated in
              any other way, you can complain to the supervisory authority. For Austria this is the Data Protection Authority, whose website you can
              find at
              <a href="https://www.data-protection-authority.gv.at/?tid=111779921" target="_blank" rel="noreferrer">
                https://www.data-protection-authority.gv.at/
              </a>
              and for Germany you can contact the
              <a href="https://www.bfdi.bund.de" target="_blank" rel="noreferrer">
                Federal Commissioner for Data Protection and Freedom of Information (BfDI)
              </a>
              .
            </p>
            <p>
              <strong>In short: </strong>You have rights – do not hesitate to contact our responsible person or authority listed above!
            </p>
            <h2 id="webhosting-en">Web hosting</h2>
            <ul>
              <li>
                <strong>Web hosting Overview</strong>
              </li>
              <li>Affected parties: visitors to the website </li>
              <li>Purpose: professional hosting of the website and security of operations </li>
              <li>
                Processed data: IP address, time of website visit, browser used and other data. You can find more details on this below or at the
                respective web hosting provider.
              </li>
              <li>Storage period: dependent on the respective provider, but usually 2 weeks </li>
              <li>Legal basis: Art. 6 para. 1 lit. f GDPR (legitimate interests) </li>
            </ul>
            <h3>What is web hosting?</h3>
            <p>
              Every time you visit a website nowadays, certain information – including personal data – is automatically created and stored, including
              on this website. This data should be processed as sparingly as possible, and only with good reason. By website, we mean the entirety of
              all websites on your domain, i.e. everything from the homepage to the very last subpage (like this one here). By domain we mean
              example.uk or examplepage.com.
            </p>
            <p>
              When you want to view a website on a screen, you use a program called a web browser. You probably know the names of some web browsers:
              Google Chrome, Microsoft Edge, Mozilla Firefox, and Apple Safari.
            </p>
            <p>
              The web browser has to connect to another computer which stores the website’s code: the web server. Operating a web server is
              complicated and time-consuming, which is why this is usually done by professional providers. They offer web hosting and thus ensure the
              reliable and flawless storage of website data.
            </p>
            <p>
              Whenever the browser on your computer establishes a connection (desktop, laptop, smartphone) and whenever data is being transferred to
              and from the web server, personal data may be processed. After all, your computer stores data, and the web server also has to retain the
              data for a period of time in order to ensure it can operate properly.
            </p>
            <p>Illustration:</p>
            <p>
              <img src="https://www.adsimple.at/wp-content/uploads/2018/03/browser-und-webserver.svg" alt="Browser and web server" />
            </p>
            <h3>Why do we process personal data?</h3>
            <p>The purposes of data processing are:</p>
            <ol>
              <li>Professional hosting of the website and operational security</li>
              <li>To maintain the operational as well as IT security</li>
              <li>Anonymous evaluation of access patterns to improve our offer, and if necessary, for prosecution or the pursuit of claims.</li>
            </ol>
            <h3>Which data are processed?</h3>
            <p>
              Even while you are visiting our website, our web server, that is the computer on which this website is saved, usually automatically
              saves data such as
            </p>
            <ul>
              <li>the full address (URL) of the accessed website (e. g. https://www.examplepage.uk/examplesubpage.html?tid=111779921)</li>
              <li>browser and browser version (e.g. Chrome 87)</li>
              <li>the operating system used (e.g. Windows 10)</li>
              <li>the address (URL) of the previously visited page (referrer URL) (e. g. https://www.examplepage.uk/icamefromhere.html/)</li>
              <li>the host name and the IP address of the device from the website is being accessed from (e.g. COMPUTERNAME and 194.23.43.121)</li>
              <li>date and time</li>
              <li>in so-called web server log files</li>
            </ul>
            <h3>How long is the data stored?</h3>
            <p>
              Generally, the data mentioned above are stored for two weeks and are then automatically deleted. We do not pass these data on to others,
              but we cannot rule out the possibility that this data may be viewed by the authorities in the event of illegal conduct.
            </p>
            <p>
              <strong>In short:</strong> Your visit is logged by our provider (company that runs our website on special computers (servers)), but we
              do not pass on your data without your consent!
            </p>
            <h3>Legal basis</h3>
            <p>
              The lawfulness of processing personal data in the context of web hosting is justified in Art. 6 para. 1 lit. f GDPR (safeguarding of
              legitimate interests), as the use of professional hosting with a provider is necessary to present the company in a safe and
              user-friendly manner on the internet, as well as to have the ability to track any attacks and claims, if necessary.
            </p>
            <h2 id="cookies-en">Cookies</h2>
            <ul>
              <li>
                <strong> Cookies Overview </strong>
              </li>
              <li>Affected parties: visitors to the website</li>
              <li>
                Purpose: depending on the respective cookie. You can find out more details below or from the software manufacturer that sets the
                cookie.
              </li>
              <li>
                Processed data: Depending on the cookie used. More details can be found below or from the manufacturer of the software that sets the
                cookie.
              </li>
              <li>Storage duration: can vary from hours to years, depending on the respective cookie</li>
              <li>Legal basis: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1 lit. f GDPR (legitimate interests)</li>
            </ul>
            <h3>What are cookies?</h3>
            <p>
              Our website uses HTTP-cookies to store user-specific data.
              <br />
              In the following we explain what cookies are and why they are used, so that you can better understand the following privacy policy.
            </p>
            <p>
              Whenever you surf the Internet, you are using a browser. Common browsers are for example, Chrome, Safari, Firefox, Internet Explorer and
              Microsoft Edge. Most websites store small text-files in your browser. These files are called cookies.
            </p>
            <p>
              It is important to note that cookies are very useful little helpers. Almost every website uses cookies. More precisely, these are HTTP
              cookies, as there are also other cookies for other uses. HTTP cookies are small files that our website stores on your computer. These
              cookie files are automatically placed into the cookie-folder, which is the “brain” of your browser. A cookie consists of a name and a
              value. Moreover, to define a cookie, one or multiple attributes must be specified.
            </p>
            <p>
              Cookies store certain user data about you, such as language or personal page settings. When you re-open our website to visit again, your
              browser submits these “user-related” information back to our site. Thanks to cookies, our website knows who you are and offers you the
              settings you are familiar to. In some browsers, each cookie has its own file, while in others, such as Firefox, all cookies are stored
              in one single file.
            </p>
            <p>
              The following graphic shows a possible interaction between a web browser such as Chrome and the web server. The web browser requests a
              website and receives a cookie back from the server. The browser then uses this again as soon as another page is requested.
            </p>
            <p>
              <img
                src="https://www.adsimple.at/wp-content/uploads/2018/03/http-cookie-interaction.svg"
                alt="HTTP cookie interaction between browser and web server"
              />
            </p>
            <p>
              There are both first-party cookies and third-party cookies. First-party cookies are created directly by our site, while third-party
              cookies are created by partner-websites (e.g. Google Analytics). Each cookie must be evaluated individually, as each cookie stores
              different data. The expiry time of a cookie also varies from a few minutes to a few years. Cookies are not software programs and do not
              contain viruses, trojans or other malware. Cookies also cannot access your PC’s information.
            </p>
            <p>
              Upon your first visit to a website you are usually asked which of these cookie-types you want to accept. Furthermore, this decision will
              of course also be stored in a cookie.
            </p>
            <p>
              If you want to learn more about cookies and do not mind technical documentation, we recommend
              <a href="https://tools.ietf.org/html/rfc6265" target="_blank" rel="noreferrer">
                https://tools.ietf.org/html/rfc6265
              </a>
              , the Request for Comments of the Internet Engineering Task Force (IETF) called “HTTP State Management Mechanism”.
            </p>
            <h3>Purpose of processing via cookies</h3>
            <p>
              The purpose ultimately depends on the respective cookie. You can find out more details below or from the software manufacturer that sets
              the cookie.
            </p>
            <h3>Which data are processed?</h3>
            <p>
              Cookies are little helpers for a wide variety of tasks. Unfortunately, it is not possible to tell which data is generally stored in
              cookies, but in the privacy policy below we will inform you on what data is processed or stored.
            </p>
            <h3>Storage period of cookies</h3>
            <p>
              The storage period depends on the respective cookie and is further specified below. Some cookies are erased after less than an hour,
              while others can remain on a computer for several years.
            </p>
            <p>
              You can also influence the storage duration yourself. You can manually erase all cookies at any time in your browser (also see “Right of
              objection” below). Furthermore, the latest instance cookies based on consent will be erased is after you withdraw your consent. The
              legality of storage will remain unaffected until then.
            </p>
            <h3>Right of objection – how can I erase cookies?</h3>
            <p>
              You can decide for yourself how and whether you want to use cookies. Regardless of which service or website the cookies originate from,
              you always have the option of erasing, deactivating or only partially accepting cookies. You can for example block third-party cookies
              but allow all other cookies.
            </p>
            <p>
              If you want to find out which cookies have been stored in your browser, or if you want to change or erase cookie settings, you can find
              this option in your browser settings:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noreferrer">
                Chrome: Clear, enable and manage cookies in Chrome
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noreferrer">
                Safari: Manage cookies and website data in Safari
              </a>
            </p>
            <p>
              <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?tid=111779921" target="_blank" rel="noreferrer">
                Firefox: Clear cookies and site data in Firefox
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Internet Explorer: Delete and manage cookies
              </a>
            </p>
            <p>
              <a href="https://support.microsoft.com/en-gb/help/4027947/microsoft-edge-delete-cookies?tid=111779921" target="_blank" rel="noreferrer">
                Microsoft Edge: Delete cookies in Microsoft Edge
              </a>
            </p>
            <p>
              If you generally do not want cookies, you can set up your browser in a way to notify you whenever a cookie is about to be set. This
              gives you the opportunity to manually decide to either permit or deny the placement of every single cookie. This procedure varies
              depending on the browser. Therefore, it might be best for you to search for the instructions in Google. If you are using Chrome, you
              could for example put the search term “delete cookies Chrome” or “deactivate cookies Chrome” into Google.
            </p>
            <h3>Legal basis</h3>
            <p>
              The so-called “cookie directive” has existed since 2009. It states that the storage of cookies requires your <strong>consent</strong>
              (Article 6 Paragraph 1 lit. a GDPR). Within countries of the EU, however, the reactions to these guidelines still vary greatly. In
              Austria, however, this directive was implemented in Section 96 (3) of the Telecommunications Act (TKG). In Germany, the cookie
              guidelines have not been implemented as national law. Instead, this guideline was largely implemented in Section 15 (3) of the Telemedia
              Act (TMG).
            </p>
            <p>
              For absolutely necessary cookies, even if no consent has been given, there are legitimate interests (Article 6 (1) (f) GDPR), which in
              most cases are of an economic nature. We want to offer our visitors a pleasant user experience on our website. For this, certain cookies
              often are absolutely necessary.
            </p>
            <p>
              This is exclusively done with your consent, unless absolutely necessary cookies are used. The legal basis for this is Article 6 (1) (a)
              of the GDPR.
            </p>
            <p>In the following sections you will find more detail on the use of cookies, provided the used software does use cookies.</p>
            <h2 id="google-analytics-datenschutzerklaerung-en">Google Analytics Privacy Policy</h2>
            <ul>
              <li>
                <strong> Google Analytics Privacy Policy Overview </strong>
              </li>
              <li>Affected parties: website visitors</li>
              <li>Purpose: Evaluation of visitor information to optimise the website.</li>
              <li>
                Processed data: Access statistics that contain data such as the location of access, device data, access duration and time, navigation
                behaviour, click behaviour and IP addresses. You can find more details on this in the privacy policy below.
              </li>
              <li>Storage period: depending on the properties used</li>
              <li>Legal basis: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1 lit. f GDPR (legitimate interests)</li>
            </ul>
            <h3>What is Google Analytics?</h3>
            <p>
              We use the tracking and analysis tool Google Analytics (GA) of the US-American company Google LLC (1600 Amphitheatre Parkway Mountain
              View, CA 94043, USA). Google Analytics collects data on your actions on our website. Whenever you click a link for example, this action
              is saved in a cookie and transferred to Google Analytics. With the help of reports which we receive from Google Analytics, we can adapt
              our website and our services better to your wishes. In the following, we will explain the tracking tool in more detail, and most of all,
              we will inform you what data is saved and how you can prevent this.
            </p>
            <p>
              Google Analytics is a tracking tool with the purpose of conducting data traffic analysis of our website. For Google Analytics to work,
              there is a tracking code integrated to our website. Upon your visit to our website, this code records various actions you perform on
              your website. As soon as you leave our website, this data is sent to the Google Analytics server, where it is stored.
            </p>
            <p>Google processes this data and we then receive reports on your user behaviour. These reports can be one of the following:</p>
            <ul>
              <li>
                Target audience reports: With the help of target audience reports we can get to know our users better and can therefore better
                understand who is interested in our service.
              </li>
              <li>Advertising reports: Through advertising reports we can analyse our online advertising better and hence improve it.</li>
              <li>
                Acquisition reports: Acquisition reports provide us helpful information on how we can get more people enthusiastic about our service.
              </li>
              <li>
                Behaviour reports: With these reports, we can find out how you interact with our website. By the means of behaviour reports, we can
                understand what path you go on our website and what links you click.
              </li>
              <li>
                Conversion reports: A conversion is the process of leading you to carry out a desired action due to a marketing message. An example of
                this would be transforming you from a mere website visitor into a buyer or a newsletter subscriber. Hence, with the help of these
                reports we can see in more detail, if our marketing measures are successful with you. Our aim is to increase our conversion rate.
              </li>
              <li>
                Real time reports: With the help of these reports we can see in real time, what happens on our website. It makes us for example see,
                we can see how many users are reading this text right now.
              </li>
            </ul>
            <h3>Why do we use Google Analytics on our website?</h3>
            <p>
              The objective of our website is clear: We want to offer you the best possible service. Google Analytics’ statistics and data help us
              with reaching this goal.
            </p>
            <p>
              Statistically evaluated data give us a clear picture of the strengths and weaknesses of our website. On the one hand, we can optimise
              our page in a way, that makes it easier to be found by interested people on Google. On the other hand, the data helps us to get a better
              understanding of you as our visitor. Therefore, we can very accurately find out what we must improve on our website, in order to offer
              you the best possible service. The analysis of that data also enables us to carry out our advertising and marketing measures in a more
              individual and more cost-effective way. After all, it only makes sense to show our products and services exclusively to people who are
              interested in them.
            </p>
            <h3>What data is stored by Google Analytics?</h3>
            <p>
              With the aid of a tracking code, Google Analytics creates a random, unique ID which is connected to your browser cookie. That way,
              Google Analytics recognises you as a new user. The next time you visit our site, you will be recognised as a “recurring” user. All data
              that is collected gets saved together with this very user ID. Only this is how it is made possible for us to evaluate and analyse
              pseudonymous user profiles.
            </p>
            <p>
              To analyse our website with Google Analytics, a property ID must be inserted into the tracking code. The data is then stored in the
              corresponding property. Google Analytics 4-property is standard for every newly created property. An alternative however, is the
              Universal Analytics Property. Depending on the property that is being used, data are stored for different periods of time.
            </p>
            <p>
              Your interactions on our website are measured by tags such as cookies and app instance IDs. Interactions are all kinds of actions that
              you perform on our website. If you are also using other Google systems (such as a Google Account), data generated by Google Analytics
              can be linked with third-party cookies. Google does not pass on any Google Analytics data, unless we as the website owners authorise it.
              In case it is required by law, exceptions can occur.
            </p>
            <p>
              Further possibly stored data include contact data, potential reviews, playing media (e.g. when you play a video on our site), sharing of
              contents via social media or adding our site to your favourites. This list is not exhaustive and only serves as general guidance on
              Google Analytics’ data retention.
            </p>
            <h3>How long and where is the data stored?</h3>
            <p>
              Google has servers across the globe. Most of them are in America and therefore your data is mainly saved on American servers. Here you
              can read detailed information on where Google’s data centres are located:
              <a href="https://www.google.com/about/datacenters/inside/locations/?hl=en" target="_blank" rel="noreferrer">
                https://www.google.com/about/datacenters/inside/locations/?hl=en
              </a>
            </p>
            <p>
              Your data is allocated to various physical data mediums. This has the advantage of allowing to retrieve the data faster, and of
              protecting it better from manipulation. Every Google data centre has respective emergency programs for your data. Hence, in case of a
              hardware failure at Google or a server error due to natural disasters, the risk for a service interruption stays relatively low.
            </p>
            <p>
              The data retention period depends on the properties used. When using the newer Google Analytics 4-properties, the retention period of
              your user data is set to 14 months. For so-called event data, we have the option of choosing a retention period of either 2 months or 14
              months.
            </p>
            <p>
              Google Analytics has a 26 months standardised period of retaining your user data. After this time, your user data is deleted. However,
              we have the possibility to choose the retention period of user data ourselves. There are the following five options:
            </p>
            <ul>
              <li>Deletion after 14 months</li>
              <li>Deletion after 26 months</li>
              <li>Deletion after 38 months</li>
              <li>Deletion after 50 months</li>
              <li>No automatical deletion</li>
            </ul>
            <p>
              Additionally, there is the option for data to be deleted only if you no longer visit our website within a period determined by us. In
              this case, the retention period will be reset every time you revisit our website within the specified period.
            </p>
            <p>
              As soon as the chosen period is expired, the data is deleted once a month. This retention period applies to any of your data which is
              linked to cookies, user identification and advertisement IDs (e.g. cookies of the DoubleClick domain). Any report results are based on
              aggregated information and are stored independently of any user data. Aggregated information is a merge of individual data into a single
              and bigger unit.
            </p>
            <h3>How can I delete my data or prevent data retention?</h3>
            <p>
              Under the provisions of the European Union’s data protection law, you have the right to obtain information on your data and to update,
              delete or restrict it. With the help of a browser add on that can deactivate Google Analytics’ JavaScript (ga.js, analytics.js, dc.js),
              you can prevent Google Analytics from using your data. You can download this add on at
              <a href="https://tools.google.com/dlpage/gaoptout?hl=en-GB" target="_blank" rel="noreferrer">
                https://tools.google.com/dlpage/gaoptout?hl=en-GB
              </a>
              . Please consider that this add on can only deactivate any data collection by Google Analytics.
            </p>
            <p>
              If you generally want to deactivate, delete or manage all cookies (independently of Google Analytics), you can use one of the guides
              that are available for any browser:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noreferrer">
                Chrome: Clear, enable and manage cookies in Chrome
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noreferrer">
                Safari: Manage cookies and website data in Safari
              </a>
            </p>
            <p>
              <a href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?tid=111779921" target="_blank" rel="noreferrer">
                Firefox: Clear cookies and site data in Firefox
              </a>
            </p>
            <p>
              <a
                href="https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Internet Explorer: Delete and manage cookies
              </a>
            </p>
            <p>
              <a href="https://support.microsoft.com/en-gb/help/4027947/microsoft-edge-delete-cookies?tid=111779921" target="_blank" rel="noreferrer">
                Microsoft Edge: Delete cookies in Microsoft Edge
              </a>
            </p>
            <h3>Legal basis</h3>
            <p>
              The use of Google Analytics requires your consent, which we obtained via our cookie popup. According to
              <strong> Art. 6 para. 1 lit. a of the GDPR (consent) </strong>, this is the legal basis for the processing of personal data when
              collected via web analytics tools.
            </p>
            <p>
              In addition to consent, we have legitimate interest in analysing the behaviour of website visitors, in order to technically and
              economically improve our offer. With Google Analytics, we can recognise website errors, identify attacks and improve profitability. The
              legal basis for this is <strong> Art. 6 para. 1 lit. f of the GDPR (legitimate interests) </strong>. Nevertheless, we only use Google
              Analytics if you have given your consent.
            </p>
            <p>
              We would like to note, that according to the European Court of Justice, there is currently no adequate level of protection for data
              transfer to the USA. Data processing is mainly done by Google. This may result in data not being anonymously processed and stored.
              Furthermore, US government authorities may get access to individual data. It may also happen for this data to get linked to data from
              other Google services you have a user account with.
            </p>
            <p>
              We hope we could provide you with the most important information about data processing by Google Analytics. If you want to find out more
              on the tracking service, we recommend these two links:
              <a href="https://marketingplatform.google.com/about/analytics/terms/gb/" target="_blank" rel="noreferrer">
                https://marketingplatform.google.com/about/analytics/terms/gb/
              </a>
              and
              <a href="https://support.google.com/analytics/answer/6004245?hl=en" target="_blank" rel="noreferrer">
                https://support.google.com/analytics/answer/6004245?hl=en
              </a>
              .
            </p>
            <h2 id="google-analytics-ip-anonymisierung-en">Google Analytics IP Anonymisation</h2>
            <p>
              We implemented Google Analytics’ IP address anonymisation to this website. Google developed this function, so this website can comply
              with the applicable privacy laws and the local data protection authorities’ recommendations, should they prohibit the retention of any
              full IP addresses.
              <br />
              The anonymisation or masking of IP addresses takes place, as soon as they reach Google Analytics’ data collection network, but before
              the data would be saved or processed.
            </p>
            <p>
              You can find more information on IP anonymisation at
              <a href="https://support.google.com/analytics/answer/2763052?hl=en" target="_blank" rel="noreferrer">
                https://support.google.com/analytics/answer/2763052?hl=en
              </a>
              .
            </p>
            <h2 id="google-tag-manager-datenschutzerklaerung-en">Google Tag Manager Privacy Policy</h2>
            <ul>
              <li>
                <strong>Google Tag Manager Privacy Policy Overview</strong>
              </li>
              <li>Affected parties: website visitors</li>
              <li>Purpose: Organisation of individual tracking tools</li>
              <li>Processed data: Google Tag Manager itself does not store any data. The data record tags of the web analytics tools used.</li>
              <li>Storage period: depending on the web analytics tool used</li>
              <li>Legal bases: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1 lit. f GDPR (legitimate interests)</li>
            </ul>
            <h3>What is Google Tag Manager?</h3>
            <p>
              We use Google Tag Manager by the company Google Inc. (1600 Amphitheatre Parkway Mountain View, CA 94043, USA) for our website.
              <br />
              This Tag Manager is one of Google’s many helpful marketing products. With it, we can centrally integrate and manage code sections of
              various tracking tools, that we use on our website.
            </p>
            <p>
              In this privacy statement we will explain in more detail, what Google Tag Manager does, why we use it and to what extent your data is
              processed.
            </p>
            <p>
              Google Tag Manager is an organising tool with which we can integrate and manage website tags centrally and via a user interface. Tags
              are little code sections which e.g. track your activities on our website. For this, segments of JavaScript code are integrated to our
              site’s source text. The tags often come from Google’s intern products, such as Google Ads or Google Analytics, but tags from other
              companies can also be integrated and managed via the manager. Since the tags have different tasks, they can collect browser data, feed
              marketing tools with data, embed buttons, set cookies and track users across several websites.
            </p>
            <h3>Why do we use Google Tag Manager for our website?</h3>
            <p>
              Everybody knows: Being organised is important! Of course, this also applies to maintenance of our website. In order to organise and
              design our website as well as possible for you and anyone who is interested in our products and services, we rely on various tracking
              tools, such as Google Analytics.
              <br />
              The collected data shows us what interests you most, which of our services we should improve, and which other persons we should also
              display our services to. Furthermore, for this tracking to work, we must implement relevant JavaScript Codes to our website. While we
              could theoretically integrate every code section of every tracking tool separately into our source text, this would take too much time
              and we would lose overview. This is the reason why we use Google Tag Manager. We can easily integrate the necessary scripts and manage
              them from one place. Additionally, Google Tag Manager’s user interface is easy to operate, and requires no programming skills.
              Therefore, we can easily keep order in our jungle of tags.
            </p>
            <h3>What data is stored by Google Tag Manager?</h3>
            <p>
              Tag Manager itself is a domain that neither uses cookies nor stores data. It merely functions as an “administrator“ of implemented tags.
              Data is collected by the individual tags of the different web analysis tools. Therefore, in Google Tag Manager the data is sent to the
              individual tracking tools and does not get saved.
            </p>
            <p>
              However, with the integrated tags of different web analysis tools such as Google Analytics, this is quite different. Depending on the
              analysis tool used, various data on your internet behaviour is collected, stored and processed with the help of cookies. Please read our
              texts on data protection for more information on the articular analysis and tracking tools we use on our website.
            </p>
            <p>
              We allowed Google via the account settings for the Tag Manager to receive anonymised data from us. However, this exclusively refers to
              the use of our Tag Manager and not to your data, which are saved via code sections. We allow Google and others, to receive selected data
              in anonymous form. Therefore, we agree to the anonymised transfer of our website data. However, even after extensive research we could
              not find out what summarised and anonymous data it is exactly that gets transmitted. What we do know is that Google deleted any info
              that could identify our website. Google combines the data with hundreds of other anonymous website data and creates user trends as part
              of benchmarking measures. Benchmarking is a process of comparing a company’s results with the ones of competitors. As a result,
              processes can be optimised based on the collected information.
            </p>
            <h3>How long and where is the data stored?</h3>
            <p>
              When Google stores data, this is done on Google’s own servers. These servers are located all over the world, with most of them being in
              America. At
              <a href="https://www.google.com/about/datacenters/inside/locations/?hl=en" target="_blank" rel="noreferrer">
                https://www.google.com/about/datacenters/inside/locations/?hl=en
              </a>
              you can read in detail where Google’s servers are.
            </p>
            <p>
              In our individual data protection texts on the different tools you can find out how long the respective tracking tools store your data.
            </p>
            <h3>How can I delete my data or prevent data retention?</h3>
            <p>
              Google Tag Manager itself does not set any cookies but manages different tracking websites’ tags. In our data protection texts on the
              different tracking tools you can find detailed information on how you can delete or manage your data.
            </p>
            <p>
              Please note that when using this tool, your data may also be stored and processed outside the EU. Most third countries (including the
              USA) are not considered secure under current European data protection law. Data must not be transferred, stored and processed to
              insecure third countries, unless there are suitable guarantees (such as EU standard contractual clauses) between us and the non-European
              service provider.
            </p>
            <h3>Legal basis</h3>
            <p>
              The use of the Google Tag Manager requires your consent, which we obtained via our cookie popup. According to
              <strong>Art. 6 para. 1 lit. a GDPR (consent)</strong>, this consent is the legal basis for personal data processing, such as when it is
              collected by web analytics tools.
            </p>
            <p>
              In addition to consent, we have a legitimate interest in analysing the behaviour of website visitors and thus technically and
              economically improving our offer. With the help of Google Tag Managers we can also improve profitability. The legal basis for this is
              <strong>Art. 6 para. 1 lit. f GDPR (legitimate interests)</strong>. We only use Google Tag Manager if you have given us your consent.
            </p>
            <p>
              We would like to note, that according to the European Court of Justice, there is currently no adequate level of protection for data
              transfer to the USA. Data processing is mainly done by Google (if e.g. Google Analytics is used in Google Tag Manager). This may lead to
              data not being anonymously processed and stored. Furthermore, US government authorities may have access to individual data. this data
              may also get linked to data from other Google services that you have a user account with.
            </p>
            <p>
              If you want to learn more about Google Tag Manager, we recommend their FAQs at
              <a href="https://support.google.com/tagmanager/?hl=en#topic=3441530" target="_ blank" rel="noopener noreferrer">
                https://support.google.com/tagmanager/?hl=en#topic=3441530
              </a>
              .
            </p>
            <h2 id="e-mail-marketing-en">Email-Marketing</h2>
            <ul>
              <li>
                <strong> Email Marketing Overview</strong>
              </li>
              <li>Affected parties: newsletter subscribers</li>
              <li>Purpose: direct marketing via email, notification of events that are relevant to the system</li>
              <li>
                Processed data: data entered during registration, but at least the email address. You can find more details on this in the respective
                email marketing tool used.
              </li>
              <li>Storage duration: for the duration of the subscription</li>
              <li>Legal bases: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1 lit. f GDPR (legitimate interests)</li>
            </ul>
            <h3>What is Email-Marketing?</h3>
            <p>
              We use email marketing to keep you up to date. If you have agreed to receive our emails or newsletters, your data will be processed and
              stored. Email marketing is a part of online marketing. In this type of marketing, news or general information about a company, product
              or service are emailed to a specific group of people who are interested in it.
            </p>
            <p>
              If you want to participate in our email marketing (usually via newsletter), you usually just have to register with your email address.
              To do this, you have to fill in and submit an online form. However, we may also ask you for your title and name, so we can address you
              personally in our emails.
            </p>
            <p>
              The registration for newsletters generally works with the help of the so-called “double opt-in procedure”. After you have registered for
              our newsletter on our website, you will receive an email, via which you can confirm the newsletter registration. This ensures that you
              own the email address you signed up with, and prevents anyone to register with a third-party email address. We or a notification tool we
              use, will log every single registration. This is necessary so we can ensure and prove, that registration processes are done legally and
              correctly. In general, the time of registration and registration confirmation are stored, as well as your IP address. Moreover, any
              change you make to your data that we have on file is also logged.
            </p>
            <h3>Why do we use Email-Marketing?</h3>
            <p>
              Of course, we want to stay in contact with you and keep you in the loop of the most important news about our company. For this, we use
              email marketing – often just referred to as “newsletters” – as an essential part of our online marketing. If you agree to this or if it
              is permitted by law, we will send you newsletters, system emails or other notifications via email. Whenever the term “newsletter” is
              used in the following text, it mainly refers to emails that are sent regularly. We of course don’t want to bother you with our
              newsletter in any way. Thus, we genuinely strive to offer only relevant and interesting content. In our emails you can e.g. find out
              more about our company and our services or products. Since we are continuously improving our offer, our newsletter will always give you
              the latest news, or special, lucrative promotions. Should we commission a service provider for our email marketing, who offers a
              professional mailing tool, we do this in order to offer you fast and secure newsletters. The purpose of our email marketing is to inform
              you about new offers and also to get closer to our business goals.
            </p>
            <h3>Which data are processed?</h3>
            <p>
              If you subscribe to our newsletter via our website, you then have to confirm your membership in our email list via an email that we will
              send to you. In addition to your IP and email address, your name, address and telephone number may also be stored. However, this will
              only be done if you agree to this data retention. Any data marked as such are necessary so you can participate in the offered service.
              Giving this information is voluntary, but failure to provide it will prevent you from using this service. Moreover, information about
              your device or the type of content you prefer on our website may also be stored. In the section “Automatic data storage” you can find
              out more about how your data is stored when you visit a website. We record your informed consent, so we can always prove that it
              complies with our laws.
            </p>
            <h3>Duration of data processing</h3>
            <p>
              If you unsubscribe from our e-mail/newsletter distribution list, we may store your address for up to three years on the basis of our
              legitimate interests, so we can keep proof your consent at the time. We are only allowed to process this data if we have to defend
              ourselves against any claims.
            </p>
            <p>
              However, if you confirm that you have given us your consent to subscribe to the newsletter, you can submit an individual request for
              erasure at any time. Furthermore, if you permanently object to your consent, we reserve the right to store your email address in a
              blacklist. But as long as you have voluntarily subscribed to our newsletter, we will of course keep your email address on file.
            </p>
            <h3>Withdrawal – how can I cancel my subscription?</h3>
            <p>
              You have the option to cancel your newsletter subscription at any time. All you have to do is revoke your consent to the newsletter
              subscription. This usually only takes a few seconds or a few clicks. Most of the time you will find a link at the end of every email,
              via which you will be able to cancel the subscription. Should you not be able to find the link in the newsletter, you can contact us by
              email and we will immediately cancel your newsletter subscription for you.
            </p>
            <h3>Legal basis</h3>
            <p>
              Our newsletter is sent on the basis of your <strong> consent </strong> (Article 6 (1) (a) GDPR). This means that we are only allowed to
              send you a newsletter if you have actively registered for it beforehand. Moreover, we may also send you advertising messages on the
              basis of Section 7 (3) UWG (Unfair Competition Act), provided you have become our customer and have not objected to the use of your
              email address for direct mail.
            </p>
            <p>
              If available – you can find information on special email marketing services and how they process personal data, in the following
              sections.
            </p>
            <h2 id="mailchimp-datenschutzerklaerung-en">MailChimp Privacy Policy</h2>
            <ul>
              <li>
                <strong> MailChimp Privacy Policy Overview</strong>
              </li>
              <li>Affected parties: newsletter subscribers</li>
              <li>Purpose: direct marketing via email, notification of events that are relevant to the system</li>
              <li>Processed data: data entered during registration, but at least the email address.</li>
              <li>Storage duration: for the subscription period</li>
              <li>Legal basis: Art. 6 para. 1 lit. a GDPR (consent), Art. 6 para. 1 lit. f GDPR (legitimate interests)</li>
            </ul>
            <h3>What is MailChimp?</h3>
            <p>
              Like many other websites, we use the services of the newsletter company MailChimp on our website. The operator of MailChimp is the
              company The Rocket Science Group, LLC, 675 Ponce de Leon Ave NE, Suite 5000, Atlanta, GA 30308 USA. With the aid of MailChimp we can
              easily send you interesting news via newsletter. For the use of the service we do not have to install anything but can still access a
              pool of very efficient features. In the following we will give more details on this email marketing service and will inform you about
              the most important data protection aspects.
            </p>
            <p>
              MailChimp is a cloud-based newsletter management service. “Cloud-based“ means that we do not need to install MailChimp on our own
              computer or server. Instead, we use the service on an external server, or more specifically via an IT infrastructure, which is available
              via the internet. Using a software this way is also called SaaS (software as a service).
            </p>
            <p>
              <img src="https://www.adsimple.at/wp-content/uploads/2018/05/mailchimp-overview.svg" alt="Systematische Funktion von mailchimp" />
            </p>
            <p>
              MailChimp allows us to chose from a wide range of different email types. Depending on what goal we want to reach with our newsletter, we
              can run individual campaigns, regular campaigns, auto responders (automated emails), A/B tests, RSS campaigns (mailings at pre-set times
              and frequencies) and follow-up campaigns.
            </p>
            <h3>Why do we use MailChimp on our website?</h3>
            <p>
              The reason we would use any newsletter service is so we can stay in contact with you. We want to keep you on the loop about what news or
              attractive offers we have for you at the time. As we constantly seek out the easiest and best solutions for our marketing measures, we
              have decided on MailChimp as our newsletter management service. While the software is very easy to use, it offers many helpful features.
              For example, it allows us to create interesting and attractive newsletters in only a short time. With integrated design templates we can
              create every newsletter in an individual way. Due to the “responsive design” feature, our contents are also presented in a readable and
              pleasant way on your smartphone (or any other mobile device).
            </p>
            <p>
              With tools such as A/B testing or the extensive analysis options, we can swiftly tell how you like our newsletters. This means that we
              can react if necessary and improve our offer or our services.
            </p>
            <p>
              Another advantage is MailChimp’s “cloud system”. The data is not stored and processed directly on our server. We can retrieve the data
              from external servers and therefore save our memory space and also decrease maintenance effort.
            </p>
            <h3>What data is stored by MailChimp?</h3>
            <p>
              Rocket Science Group LLC (MailChimp) operate online platforms which enable us to get in contact with you, provided you subscribed to our
              newsletter. If you become a subscriber of our newsletter via our website, by email you agree to become a member of a MailChimp email
              list. Then, MailChimp saves your subscription data and your IP address, so it can verify your entry into the list provider. Moreover,
              MailChimp stores your email address, your name, your physical address and demographic information, such as language or location.
            </p>
            <p>This information is used to send emails to you and to allow certain other MailChimp functions (e.g. the evaluation of newsletters).</p>
            <p>
              MailChimp also shares information with third parties to improve its services. Moreover, MailChimp shares certain data with advertising
              partners of third parties to get a better understanding of its clients’ interests, in order to provide relevant contents and
              target-oriented advertising.
            </p>
            <p>
              With so-called “web beacons” (small graphics in HTML emails), MailChimp can determine if an email has arrived, has been opened or if
              links have been clicked. This information is then stored on MailChimp’s servers. That way we receive statistical evaluations and can see
              how you liked our newsletter. Therefore, we can tailor our offer better to your wishes and improve our service.
            </p>
            <p>
              Moreover, MailChimp are allowed to use this data for improving their own service. Thus, they can for example technically optimise the
              distribution or determine the location (or the country) of the recipient.
            </p>
            <p>
              For better display you might sometimes open our newsletter via a specified link. This can be the case if your email program does not
              work or if the newsletter is not displayed correctly. The newsletter will then be shown via a MailChimp website. MailChimp also uses
              cookies on its websites (small text files which save data on your browser).
              <br />
              Personal data can be processed by MailChimp and their partners (e.g. Google Analytics). MailChimp is responsible for the collection of
              this data and we have no influence on it. MailChimp’s “Cookie Statement” (at:
              <a href="https://mailchimp.com/legal/cookies/" target="_blank" rel="noreferrer">
                https://mailchimp.com/legal/cookies/
              </a>
              ) tells you exactly how and why the company uses cookies.
            </p>
            <h3>How long and where is the data stored?</h3>
            <p>Since MailChimp is an American company, all retained data is stored on American servers.</p>
            <p>
              Generally, the data stays permanently stored on MailChimp’s servers and is deleted only when you request it. You can have your contact
              information with us deleted. This permanently removes all your personal data for us and anonymises you in MailChimp’s reports. However,
              you can also request the erasure of your data permanently at MailChimp. Then all your data are removed from there and we receive a
              notification from MailChimp. After we receive the email we have 30 days to delete your contact details from all integrations.
            </p>
            <h3>How can I erase my data or prevent data retention?</h3>
            <p>
              You can withdraw your approval for the receipt of our newsletters anytime, by clicking the link in the lower area of the received
              newsletter email. When you click on the unsubscribe link, your data with MailChimp gets deleted.
            </p>
            <p>
              When you land on a MailChimp website via a link in our newsletter and cookies are consequently set in your browser, you can delete or
              deactivate these cookies anytime.
            </p>
            <p>
              Depending on the browser, the deactivation or deletion differs slightly. The following instructions show how to manage cookies in your
              browser:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noreferrer">
                Chrome: Clear, enable and manage cookies in Chrome
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noreferrer">
                Safari: Manage cookies and website data in Safari
              </a>
            </p>
            <p>
              <a href=" https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?tid=111779921" target="_blank" rel="noreferrer">
                Firefox: Clear cookies and site data in Firefox
              </a>
            </p>
            <p>
              <a
                href=" https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Internet Explorer: Delete and manage cookies
              </a>
            </p>
            <p>
              <a
                href=" https://support.microsoft.com/en-gb/help/4027947/microsoft-edge-delete-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft Edge: Delete cookies in Microsoft Edge
              </a>
            </p>
            <p>
              If you generally do not want to allow any cookies, you can set up your browser in a way so it would notify you whenever a potential
              cookie is about to be set. This lets you decide upon the placement of every single cookie.
            </p>
            <h3>Legal basis</h3>
            <p>
              MailChimp sends our newsletter on the basis of your <strong> consent </strong> (Article 6 (1) (a) GDPR). This means that we are only
              allowed to mail you a newsletter if you have actively registered for it beforehand. If consent is not required, the newsletter is sent
              on the basis of <strong>legitimate interest </strong> in direct marketing (Article 6 (1) (f)), provided this is legally permitted. We
              record your registration process so we can keep proof of compliance with our laws.
            </p>
            <p>
              We would like to note, that according to the European Court of Justice, there is currently no adequate level of protection for data
              transfer to the USA. Data processing is mainly done by MailChimp. This may lead to data not being anonymously processed and stored.
              Furthermore, US government authorities may get access to individual data. The data may also get linked to data from other possible
              MailChimp services that you have a user account with.
            </p>
            <p>
              You can find out more on MailChimp’s use of cookies at
              <a href="https://mailchimp.com/legal/cookies/" target="_blank" rel="noopener noreferrer">
                https://mailchimp.com/legal/cookies/
              </a>
              . Furthermore, at
              <a href="https://mailchimp.com/legal/privacy/" target="_blank" rel="noopener noreferrer">
                https://mailchimp.com/legal/privacy/
              </a>
              you can find more information on data protection at MailChimp (Privacy).
            </p>
            <h2 id="openstreetmap-datenschutzerklaerung-en">OpenStreetMap Privacy Policy</h2>
            <p>
              We have included map sections of the online map tool “OpenStreetMap” to our website. It is a so-called open source mapping, which we can
              access via an API (interface). This feature is offered by OpenStreetMap Foundation, St John’s Innovation Center, Cowley Road, Cambridge,
              CB4 0WS, United Kingdom. By using this map function, your IP address will be forwarded to OpenStreetMap. In this privacy policy we will
              explain why we use the functions of the OpenStreetMap tool, where which data is stored and how you can prevent data storage.
            </p>
            <h3>What is OpenStreetMap?</h3>
            <p>
              The OpenStreetMap project was launched in 2004. Its aim is to create a free world map. Users all around the world have been collecting
              data about buildings, forests, rivers and roads. Therefore, an extensive digital world map has been created by users over the years. Of
              course, the map is not complete, but it contains a lot of data for most regions.
            </p>
            <h3>Why do we use OpenStreetMap on our website?</h3>
            <p>
              The primary intention of your website is to be helpful to you. We think this can only be the case when information can be found quickly
              and easily. On the one hand, of course, this concerns our services and products. On the other hand, there should also be other helpful
              information available to you. That is why we also use OpenStreetMap’s map service. Thanks to this, we can for example show you exactly
              how to find our company. The map shows you the best way to get to us and makes your journey very smooth and easy.
            </p>
            <h3>What data is stored by OpenStreetMap?</h3>
            <p>
              When you visit one of our websites that include OpenStreetMap, your user data is transmitted to the service where it is stored.
              OpenStreetMap collects information about your interactions with the digital map, your IP address, your browser, device type, operating
              system and on which day and at what time you used the service. Tracking software is also used to record user interactions. For this
              regard, the company specifiies the “Piwik” analysis tool in its own privacy policy.
            </p>
            <p>
              The collected data are then accessible to the relevant employee groups of the OpenStreetMap Foundation. According to the company,
              personal data will not be passed on to other people or companies, unless it is legally required. The third-party provider Piwik stores
              your IP address, but in a shortened form.
            </p>
            <h3>How long and where are the data stored?</h3>
            <p>
              The API servers, databases, and servers of auxiliary services are currently located in the United Kingdom (Great Britain and Northern
              Ireland) and the Netherlands. Your IP address and user information, which are saved in an abbreviated version by the web analysis tool
              Piwik, will be deleted after 180 days.
            </p>
            <h3>How can I delete my data or prevent data retention?</h3>
            <p>
              You have the right to both access your personal data and to object to its use and processing at any time. You can also always manage,
              delete, or deactivate cookies that may be set by OpenStreetMap in your browser. As a result, however, the service may no longer work to
              their full extent. The management, deletion or deactivation of cookies works differently in every browser. Below you will find links to
              the instructions of the most popular browsers:
            </p>
            <p>
              <a href="https://support.google.com/chrome/answer/95647?tid=111779921" target="_blank" rel="noreferrer">
                Chrome: Clear, enable and manage cookies in Chrome
              </a>
            </p>
            <p>
              <a href="https://support.apple.com/en-gb/guide/safari/sfri11471/mac?tid=111779921" target="_blank" rel="noreferrer">
                Safari: Manage cookies and website data in Safari
              </a>
            </p>
            <p>
              <a href=" https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox?tid=111779921" target="_blank" rel="noreferrer">
                Firefox: Clear cookies and site data in Firefox
              </a>
            </p>
            <p>
              <a
                href=" https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Internet Explorer: Delete and manage cookies
              </a>
            </p>
            <p>
              <a
                href=" https://support.microsoft.com/en-gb/help/4027947/microsoft-edge-delete-cookies?tid=111779921"
                target="_blank"
                rel="noreferrer"
              >
                Microsoft Edge: Delete cookies in Microsoft Edge
              </a>
            </p>
            <p>
              For more information on OpenStreetMap’s data processing, we recommend the company’s privacy policy at
              <a href="https://wiki.osmfoundation.org/wiki/Privacy_Policy?tid=111779921" target="_blank" rel="noopener noreferrer">
                https://wiki.osmfoundation.org/wiki/Privacy_Policy
              </a>
              .
            </p>
            <h2 id="google-fonts-datenschutzerklaerung-en">Google Fonts Privacy Policy</h2>
            <p>On our website we use Google Fonts, from the company Google Inc. (1600 Amphitheatre Parkway Mountain View, CA 94043, USA).</p>
            <p>
              To use Google Fonts, you must log in and set up a password. Furthermore, no cookies will be saved in your browser. The data (CSS, Fonts)
              will be requested via the Google domains fonts.googleapis.com and fonts.gstatic.com. According to Google, all requests for CSS and fonts
              are fully separated from any other Google services. If you have a Google account, you do not need to worry that your Google account
              details are transmitted to Google while you use Google Fonts. Google records the use of CSS (Cascading Style Sheets) as well as the
              utilised fonts and stores these data securely. We will have a detailed look at how exactly the data storage works.
            </p>
            <h3>What are Google Fonts?</h3>
            <p>Google Fonts (previously Google Web Fonts) is a list of over 800 fonts which Google LLC provides its users for free.</p>
            <p>
              Many of these fonts have been published under the SIL Open Font License license, while others have been published under the Apache
              license. Both are free software licenses.
            </p>
            <h3>Why do we use Google Fonts on our website?</h3>
            <p>
              With Google Fonts we can use different fonts on our website and do not have to upload them to our own server. Google Fonts is an
              important element which helps to keep the quality of our website high. All Google fonts are automatically optimised for the web, which
              saves data volume and is an advantage especially for the use of mobile terminal devices. When you use our website, the low data size
              provides fast loading times. Moreover, Google Fonts are secure Web Fonts. Various image synthesis systems (rendering) can lead to errors
              in different browsers, operating systems and mobile terminal devices. These errors could optically distort parts of texts or entire
              websites. Due to the fast Content Delivery Network (CDN) there are no cross-platform issues with Google Fonts. All common browsers
              (Google Chrome, Mozilla Firefox, Apple Safari, Opera) are supported by Google Fonts, and it reliably operates on most modern mobile
              operating systems, including Android 2.2+ and iOS 4.2+ (iPhone, iPad, iPod). We also use Google Fonts for presenting our entire online
              service as pleasantly and as uniformly as possible.
            </p>
            <h3>Which data is saved by Google?</h3>
            <p>
              Whenever you visit our website, the fonts are reloaded by a Google server. Through this external cue, data gets transferred to Google’s
              servers. Therefore, this makes Google recognise that you (or your IP-address) is visiting our website. The Google Fonts API was
              developed to reduce the usage, storage and gathering of end user data to the minimum needed for the proper depiction of fonts. What is
              more, API stands for „Application Programming Interface“ and works as a software data intermediary.
            </p>
            <p>
              Google Fonts stores CSS and font requests safely with Google, and therefore it is protected. Using its collected usage figures, Google
              can determine how popular the individual fonts are. Google publishes the results on internal analysis pages, such as Google Analytics.
              Moreover, Google also utilises data of ist own web crawler, in order to determine which websites are using Google fonts. This data is
              published in Google Fonts’ BigQuery database. Enterpreneurs and developers use Google’s webservice BigQuery to be able to inspect and
              move big volumes of data.
            </p>
            <p>
              One more thing that should be considered, is that every request for Google Fonts automatically transmits information such as language
              preferences, IP address, browser version, as well as the browser’s screen resolution and name to Google’s servers. It cannot be clearly
              identified if this data is saved, as Google has not directly declared it.
            </p>
            <h3>How long and where is the data stored?</h3>
            <p>
              Google saves requests for CSS assets for one day in a tag on their servers, which are primarily located outside of the EU. This makes it
              possible for us to use the fonts by means of a Google stylesheet. With the help of a stylesheet, e.g. designs or fonts of a website can
              get changed swiftly and easily.
            </p>
            <p>
              Any font related data is stored with Google for one year. This is because Google’s aim is to fundamentally boost websites’ loading
              times. With millions of websites referring to the same fonts, they are buffered after the first visit and instantly reappear on any
              other websites that are visited thereafter. Sometimes Google updates font files to either reduce the data sizes, increase the language
              coverage or to improve the design.
            </p>
            <h3>How can I delete my data or prevent it being stored?</h3>
            <p>
              The data Google stores for either a day or a year cannot be deleted easily. Upon opening the page this data is automatically transmitted
              to Google. In order to clear the data ahead of time, you have to contact Google’s support at
              <a href="https://support.google.com/?hl=en-GB&tid=111779921">https://support.google.com/?hl=en-GB&tid=111779921</a>. The only way for
              you to prevent the retention of your data is by not visiting our website.
            </p>
            <p>
              Unlike other web fonts, Google offers us unrestricted access to all its fonts. Thus, we have a vast sea of font types at our disposal,
              which helps us to get the most out of our website. You can find out more answers and information on Google Fonts at
              <a href="https://developers.google.com/fonts/faq?tid=111779921">https://developers.google.com/fonts/faq?tid=111779921</a>. While Google
              does address relevant elements on data protection at this link, it does not contain any detailed information on data retention.
              <br />
              It proofs rather difficult to receive any precise information on stored data by Google.
            </p>
            <p>
              On <a href="https://policies.google.com/privacy?hl=de&tid=111779921">https://policies.google.com/privacy?hl=en-GB</a> you can read more
              about what data is generally collected by Google and what this data is used for.
            </p>
            <h2 id="font-awesome-datenschutzerklaerung-en">Font Awesome Privacy Policy</h2>
            <p>
              On our website we use Font Awesome by the American company Fonticons (307 S. Main St., Suite 202, Bentonville, AR 72712, USA). Upon your
              visit to one of our websites, the Font Awesome web font, i.e. the icons, are loaded via the Font Awesome Content Delivery Network (CDN).
              This way texts, fonts and icons are displayed appropriately on every device. In this privacy policy we will go into more detail on data
              storage and data processing by this service.
            </p>
            <h3>What is Font Awesome?</h3>
            <p>
              Icons play an increasingly important role on websites. Font Awesome is a web font specifically designed for web designers and web
              developers. With Font Awesome icons can for example be scaled and coloured as desired using the CSS stylesheet language. Thus, they now
              replace old picture icons. Font Awesome CDN is the easiest way to load icons or fonts onto your website. To do this, we only had to
              embed a short line of code into our website.
            </p>
            <h3>Why do we use Font Awesome on our website?</h3>
            <p>
              Font Awesome enables our websites’ content to be depicted better. This eases your navigation on our website, and helps you grasp its
              content better. The icons can sometimes even be used to replace whole words and save space. This is particularly useful when optimising
              content specifically for smartphones. The icons are inserted as HMTL code instead of as an image, which allows us to edit the icons with
              CSS exactly as we want. Simultaneously, Font Awesome also lets us improve our loading speed, as it only contains HTML elements and no
              icon images. All these advantages help us to make our website even clearer, faster and more refined for you.
            </p>
            <h3>Which data are stored by Font Awesome?</h3>
            <p>
              The Font Awesome Content Delivery Network (CDN) is used to load icons and symbols. CDNs are networks of servers that are distributed
              around the world. They make it possible to quickly load files from locations in close proximity. When you open one of our pages, the
              respective icons will be provided by Font Awesome.
            </p>
            <p>
              For the web fonts to be loaded, your browser has to connect to the servers of Fonticons, Inc. For this, your IP address will be
              identified. Font Awesome also collects data on which icon files are downloaded, as well as when they are downloaded. Furthermore,
              technical data such as your browser version, screen resolution or the time when you accessed the page are also transmitted.
            </p>
            <p>These data are collected and stored for the following reasons:</p>
            <ul>
              <li>to optimise Content Delivery Networks</li>
              <li>to identify and fix technical errors</li>
              <li>to protect CDNs from misuse and attacks</li>
              <li>to calculate fees from Font Awesome Pro customers</li>
              <li>to identify the popularity of icons</li>
              <li>to establish which computer and software you are using</li>
            </ul>
            <p>
              If your browser does not allow web fonts, one of your PC’s standard fonts will be used automatically. Moreover, as far as we are
              currently aware, no cookies will be set. We are keeping in contact with Font Awesome’s privacy department and will let you know as soon
              as we find out more.
            </p>
            <h3>How long and where are the data stored?</h3>
            <p>
              Font Awesome stores data about the use of the Content Delivery Network also on servers in the United States of America. However, the CDN
              servers are located all across the world and store user data in your proximity. The data is usually only stored for a few weeks in an
              identifiable form. Aggregated statistics on the use of the CDNs may also be stored for longer. However, these do not include any
              personal data.
            </p>
            <h3>How can I delete my data or prevent data retention?</h3>
            <p>
              As far as we are aware, Font Awesome does not store any personal data via Content Delivery Networks. If you do not want data about the
              used icons to be stored, you will unfortunately not be able to visit our website. If your browser does not allow web fonts, no data will
              be transmitted or saved. In this case your computer’s default font will be used.
            </p>
            <p>
              If you want to find out more about Font Awesome and their data handling, we recommend you to read their privacy policy at
              <a href="https://fontawesome.com/privacy">https://fontawesome.com/privacy</a> along with the help page at
              <a href="https://fontawesome.com/help?tid=111779921">https://fontawesome.com/help</a>.
            </p>
            <h2 id="google-recaptcha-datenschutzerklaerung-en">Google reCAPTCHA Privacy Policy</h2>
            <p>
              Our primary goal is to provide you an experience on our website that is as secure and protected as possible. To do this, we use Google
              reCAPTCHA from Google Inc. (1600 Amphitheater Parkway Mountain View, CA 94043, USA). With reCAPTCHA we can determine whether you are a
              real person from flesh and bones, and not a robot or a spam software. By spam we mean any electronically undesirable information we
              receive involuntarily. Classic CAPTCHAS usually needed you to solve text or picture puzzles to check. But thanks to Google’s reCAPTCHA
              you usually do have to do such puzzles. Most of the times it is enough to simply tick a box and confirm you are not a bot. With the new
              Invisible reCAPTCHA version you don’t even have to tick a box. In this privacy policy you will find out how exactly this works, and what
              data is used for it.
            </p>
            <h3>What is reCAPTCHA?</h3>
            <p>
              reCAPTCHA is a free captcha service from Google that protects websites from spam software and misuse by non-human visitors. This service
              is used the most when you fill out forms on the Internet. A captcha service is a type of automatic Turing-test that is designed to
              ensure specific actions on the Internet are done by human beings and not bots. During the classic Turing-test (named after computer
              scientist Alan Turing), a person differentiates between bot and human. With Captchas, a computer or software program does the same.
              Classic captchas function with small tasks that are easy to solve for humans but provide considerable difficulties to machines. With
              reCAPTCHA, you no longer must actively solve puzzles. The tool uses modern risk techniques to distinguish people from bots. The only
              thing you must do there, is to tick the text field “I am not a robot”. However, with Invisible reCAPTCHA even that is no longer
              necessary. reCAPTCHA, integrates a JavaScript element into the source text, after which the tool then runs in the background and
              analyses your user behaviour. The software calculates a so-called captcha score from your user actions. Google uses this score to
              calculate the likelihood of you being a human, before entering the captcha. reCAPTCHA and Captchas in general are used every time bots
              could manipulate or misuse certain actions (such as registrations, surveys, etc.).
            </p>
            <h3>Why do we use reCAPTCHA on our website?</h3>
            <p>
              We only want to welcome people from flesh and bones on our side and want bots or spam software of all kinds to stay away. Therefore, we
              are doing everything we can to stay protected and to offer you the highest possible user friendliness. For this reason, we use Google
              reCAPTCHA from Google. Thus, we can be pretty sure that we will remain a “bot-free” website. Using reCAPTCHA, data is transmitted to
              Google to determine whether you genuinely are human. reCAPTCHA thus ensures our website’s and subsequently your security. Without
              reCAPTCHA it could e.g. happen that a bot would register as many email addresses as possible when registering, in order to subsequently
              “spam” forums or blogs with unwanted advertising content. With reCAPTCHA we can avoid such bot attacks.
            </p>
            <h3>What data is stored by reCAPTCHA?</h3>
            <p>
              reCAPTCHA collects personal user data to determine whether the actions on our website are made by people. Thus, IP addresses and other
              data Google needs for its reCAPTCHA service, may be sent to Google. Within member states of the European Economic Area, IP addresses are
              almost always compressed before the data makes its way to a server in the USA.
              <br />
              Moreover, your IP address will not be combined with any other of Google’s data, unless you are logged into your Google account while
              using reCAPTCHA. Firstly, the reCAPTCHA algorithm checks whether Google cookies from other Google services (YouTube, Gmail, etc.) have
              already been placed in your browser. Then reCAPTCHA sets an additional cookie in your browser and takes a snapshot of your browser
              window.
            </p>
            <p>
              The following list of collected browser and user data is not exhaustive. Rather, it provides examples of data, which to our knowledge,
              is processed by Google.
            </p>
            <ul>
              <li>Referrer URL (the address of the page the visitor has come from)</li>
              <li>IP-address (z.B. 256.123.123.1)</li>
              <li>
                Information on the operating system (the software that enables the operation of your computers. Popular operating systems are Windows,
                Mac OS X or Linux)
              </li>
              <li>Cookies (small text files that save data in your browser)</li>
              <li>Mouse and keyboard behaviour (every action you take with your mouse or keyboard is stored)</li>
              <li>Date and language settings (the language and date you have set on your PC is saved)</li>
              <li>
                All Javascript objects (JavaScript is a programming language that allows websites to adapt to the user. JavaScript objects can collect
                all kinds of data under one name)
              </li>
              <li>Screen resolution (shows how many pixels the image display consists of)</li>
            </ul>
            <p>
              Google may use and analyse this data even before you click on the “I am not a robot” checkmark. In the Invisible reCAPTCHA version,
              there is no need to even tick at all, as the entire recognition process runs in the background. Moreover, Google have not given details
              on what information and how much data they retain.
            </p>
            <h3>How long and where are the data stored?</h3>
            <p>
              Due to the integration of reCAPTCHA, your data will be transferred to the Google server. Google have not disclosed where exactly this
              data is stored, despite repeated inquiries. But even without confirmation from Google, it can be assumed that data such as mouse
              interaction, length of stay on a website or language settings are stored on the European or American Google servers. The IP address that
              your browser transmits to Google does generally not get merged with other Google data from the company’s other services.
              <br />
              However, the data will be merged if you are logged in to your Google account while using the reCAPTCHA plug-in.
              <strong>Google’s diverging privacy policy applies for this.</strong>
            </p>
            <h3>How can I delete my data or prevent data storage?</h3>
            <p>
              If you want to prevent any data about you and your behaviour to be transmitted to Google, you must fully log out of Google and delete
              all Google cookies before visiting our website or use the reCAPTCHA software. Generally, the data is automatically sent to Google as
              soon as you visit our website. To delete this data, you must contact Google Support at
              <a href="https://support.google.com/?hl=en-GB&tid=111779921">https://support.google.com/?hl=en-GB&tid=111779921</a>.
            </p>
            <p>If you use our website, you agree that Google LLC and its representatives automatically collect, edit and use data.</p>
            <p>
              You can find out more about reCAPTCHA on Google’s Developers page at
              <a href="https://developers.google.com/recaptcha/" target="_blank" rel="noreferrer">
                https://developers.google.com/recaptcha/
              </a>
              . While Google do give more detail on the technical development of reCAPTCHA there, they have not disclosed precise information about
              data retention and data protection. A good, basic overview of the use of data however, can be found in the company’s internal privacy
              policy at
              <a href="https://policies.google.com/privacy?hl=en-GB&tid=111779921" target="_blank" rel="noreferrer">
                https://policies.google.com/privacy?hl=en-GB
              </a>
              .
            </p>
            All texts are copyrighted.
            <p className="text-xs">
              Source: Created with the
              <a href="https://www.adsimple.at/datenschutz-generator/" target="_blank" rel="noreferrer">
                Datenschutz Generator
              </a>
              by AdSimple
            </p>
          </div>
        </section>
      </main>
    </Layout>
  );
}
